import { useRef, useEffect, useState, useCallback, MouseEvent } from 'react';
import { Viewport, BrushSettings, EditorTool, MeasureTool } from '../../types/mapData';
import { PropToolSettings } from '../../types/prop';
import { PenToolSettings, AnchorPoint } from '../../types/penPath';
import { ShapeToolSettings, ShapeType, ShapeParams, createDefaultShapeParams, isPointInShape } from '../../types/shape';
import { PropInstance, Layer, TextInstance, PenPathInstance, ShapeInstance } from '../../services/api';
import { TilePyramidManager } from '../../services/TilePyramidManager';
import { drawPenPath, drawPathPreview } from './penPathRenderer';
import { drawShape, drawShapePreview, getShapeHandles } from './shapeRenderer';
import './MapCanvas.css';

interface Point {
  x: number;
  y: number;
}

/** Handle types for prop manipulation */
type PropHandle = 'tl' | 'tr' | 'bl' | 'br' | 'rotate';

/** Handle types for text manipulation */
type TextHandle = 'tl' | 'tr' | 'bl' | 'br' | 'rotate';

/** Handle types for shape manipulation */
type ShapeHandle = 'tl' | 'tr' | 'bl' | 'br' | 't' | 'r' | 'b' | 'l' | 'rotate' | 'cornerRadius' | 'starInnerRadius';

interface MapCanvasProps {
  mapId: string;
  brushSettings: BrushSettings;
  activeTool: EditorTool;
  onDraw?: () => void;
  materialImages: Map<string, HTMLImageElement>;
  brushImages: Map<string, HTMLImageElement>;
  tileManager: TilePyramidManager | null;
  backgroundMaterial: string | null;
  /** Custom background color (hex format, e.g., "#1a1a1a") */
  backgroundColor: string | null;
  backgroundRandomRotation: boolean;
  showGrid: boolean;
  /** Pixels per meter (default 128) */
  scale: number;
  /** Callback to report current zoom level */
  onZoomChange?: (zoom: number) => void;
  /** Callback to report current mouse world position */
  onMouseWorldPosChange?: (pos: { x: number; y: number } | null) => void;
  /** Active measurement tool */
  activeMeasureTool?: MeasureTool;
  /** Props data */
  props?: PropInstance[];
  /** Prop images */
  propImages?: Map<string, HTMLImageElement>;
  /** Prop tool settings */
  propSettings?: PropToolSettings;
  /** Currently selected prop instances (multiple selection) */
  selectedPropIds?: string[];
  /** Currently active layer ID (for determining which props are interactive) */
  activeLayerId?: string;
  /** Layer data (for opacity) */
  layers?: Layer[];
  /** Callback when prop is placed */
  onPropPlace?: (x: number, y: number) => void;
  /** Callback when props are selected (Shift+Click) - also switches to prop tool */
  onPropSelect?: (propIds: string[], switchTool?: boolean) => void;
  /** Callback when props are moved - receives map of propId -> new position */
  onPropMove?: (positions: Map<string, { x: number; y: number }>) => void;
  /** Callback when props are transformed (scale/rotation) */
  onPropTransform?: (propIds: string[], scale: number, scaleX: number, scaleY: number, rotation: number) => void;
  /** Callback when viewport changes (for lazy loading) */
  onViewportChange?: (x: number, y: number, width: number, height: number, zoom: number) => void;
  /** Text instances */
  texts?: TextInstance[];
  /** Currently selected text IDs */
  selectedTextIds?: string[];
  /** Text ID being edited inline */
  editingTextId?: string | null;
  /** Callback when text is placed */
  onTextPlace?: (x: number, y: number) => void;
  /** Callback when texts are selected */
  onTextSelect?: (textIds: string[], switchTool?: boolean) => void;
  /** Callback when texts are moved */
  onTextMove?: (positions: Map<string, { x: number; y: number }>) => void;
  /** Callback when text is transformed (resize/rotation) */
  onTextTransform?: (textIds: string[], width: number, height: number, rotation: number) => void;
  /** Callback to start inline text editing */
  onTextStartEdit?: (textId: string) => void;
  /** Callback when inline text editing finishes */
  onTextFinishEdit?: (textId: string, newContent: string) => void;
  /** Pen path instances */
  penPaths?: PenPathInstance[];
  /** Currently selected pen path IDs */
  selectedPenPathIds?: string[];
  /** Pen tool settings */
  penSettings?: PenToolSettings;
  /** Whether a path is currently being drawn */
  isDrawingPath?: boolean;
  /** Current path points being drawn */
  currentPathPoints?: AnchorPoint[];
  /** Callback when a pen path is finished */
  onPenPathPlace?: (points: AnchorPoint[], closed: boolean) => void;
  /** Callback when pen paths are selected */
  onPenPathSelect?: (pathIds: string[], switchTool?: boolean) => void;
  /** Callback when pen path drag starts */
  onPenPathDragStart?: (pathIds: string[]) => void;
  /** Callback when pen paths are moved (delta from drag start) */
  onPenPathMove?: (pathIds: string[], deltaX: number, deltaY: number) => void;
  /** Callback when pen path drag ends */
  onPenPathDragEnd?: (pathIds: string[]) => void;
  /** Callback when pen paths are transformed */
  onPenPathTransform?: (pathIds: string[], scale: number, rotation: number) => void;
  /** Callback to update a pen path's points (for anchor/handle manipulation) */
  onPenPathPointsUpdate?: (pathId: string, points: AnchorPoint[]) => void;
  /** Callback to update current path points during drawing */
  onCurrentPathUpdate?: (points: AnchorPoint[]) => void;
  /** Callback when drawing state changes */
  onDrawingPathChange?: (isDrawing: boolean) => void;
  /** Currently selected anchor point indices for pen path editing */
  selectedAnchorIndices?: Map<string, Set<number>>;
  /** Callback when anchor point selection changes */
  onAnchorSelect?: (selection: Map<string, Set<number>>) => void;
  /** Callback to delete selected anchor points */
  onDeleteSelectedAnchors?: () => void;
  /** Shape instances */
  shapes?: ShapeInstance[];
  /** Currently selected shape IDs */
  selectedShapeIds?: string[];
  /** Shape tool settings */
  shapeSettings?: ShapeToolSettings;
  /** Whether a shape is currently being drawn */
  isDrawingShape?: boolean;
  /** Callback when a shape is placed */
  onShapePlace?: (shapeType: ShapeType, shapeParams: ShapeParams, x: number, y: number) => void;
  /** Callback when shapes are selected */
  onShapeSelect?: (shapeIds: string[], switchTool?: boolean) => void;
  /** Callback when shapes are moved */
  onShapeMove?: (positions: Map<string, { x: number; y: number }>) => void;
  /** Callback when shapes are transformed */
  onShapeTransform?: (shapeIds: string[], scale: number, rotation: number) => void;
  /** Callback when shape parameters are updated (from handles) */
  onShapeParamsUpdate?: (shapeId: string, shapeParams: ShapeParams) => void;
  /** Callback when drawing shape state changes */
  onDrawingShapeChange?: (isDrawing: boolean) => void;
  /** Callback when an element is clicked in view mode (for layer description viewing) */
  onLayerElementClick?: (layerId: string, elementType: 'prop' | 'text' | 'path' | 'shape', elementId: string) => void;
  /** Initial viewport position and zoom from URL params (for shareable links) */
  initialViewport?: {
    x: number;
    y: number;
    zoom: number;
  };
}

/**
 * Simple seeded pseudo-random number generator (Mulberry32)
 */
function createSeededRandom(seed: number): () => number {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * Generate a numeric seed from a string (map ID)
 */
function stringToSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// Viewport zoom limits (screen pixels per world unit)
// MIN_ZOOM = 1% means very zoomed out (1 world unit = 0.01 screen pixels)
// MAX_ZOOM = very zoomed in
const MIN_VIEWPORT_ZOOM = 0.01;
const MAX_VIEWPORT_ZOOM = 50000000;
const ZOOM_SENSITIVITY = 0.001;
const STROKE_SPACING_FACTOR = 0.3;

/**
 * Main map canvas component with pan/zoom and tile-based rendering
 */
function MapCanvas({
  mapId,
  brushSettings,
  activeTool,
  onDraw,
  materialImages,
  brushImages,
  tileManager,
  backgroundMaterial,
  backgroundColor,
  backgroundRandomRotation,
  showGrid,
  scale,
  onZoomChange,
  onMouseWorldPosChange,
  activeMeasureTool,
  props = [],
  propImages,
  propSettings,
  selectedPropIds = [],
  activeLayerId = 'default',
  layers = [],
  onPropPlace,
  onPropSelect,
  onPropMove,
  onPropTransform,
  onViewportChange,
  texts = [],
  selectedTextIds = [],
  editingTextId,
  onTextPlace,
  onTextSelect,
  onTextMove,
  onTextTransform,
  onTextStartEdit,
  onTextFinishEdit,
  penPaths = [],
  selectedPenPathIds = [],
  penSettings,
  isDrawingPath = false,
  currentPathPoints = [],
  onPenPathPlace,
  onPenPathSelect,
  onPenPathDragStart,
  onPenPathMove,
  onPenPathDragEnd,
  onPenPathTransform,
  onPenPathPointsUpdate,
  onCurrentPathUpdate,
  onDrawingPathChange,
  selectedAnchorIndices = new Map(),
  onAnchorSelect,
  onDeleteSelectedAnchors,
  shapes = [],
  selectedShapeIds = [],
  shapeSettings,
  isDrawingShape: _isDrawingShape = false,
  onShapePlace,
  onShapeSelect,
  onShapeMove,
  onShapeTransform,
  onShapeParamsUpdate,
  onDrawingShapeChange,
  onLayerElementClick,
  initialViewport
}: MapCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [viewport, setViewport] = useState<Viewport>(() => {
    // Use initial viewport from URL params if provided, otherwise default to origin
    if (initialViewport) {
      return {
        x: initialViewport.x,
        y: initialViewport.y,
        zoom: initialViewport.zoom
      };
    }
    return { x: 0, y: 0, zoom: 1 };
  });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [isDrawingActive, setIsDrawingActive] = useState<boolean>(false);
  const [lastMousePos, setLastMousePos] = useState<{ x: number; y: number } | null>(null);
  const [lastDrawPos, setLastDrawPos] = useState<{ x: number; y: number } | null>(null);
  const [mouseWorldPos, setMouseWorldPos] = useState<{ x: number; y: number } | null>(null);

  // View mode hover state - tracks layerId of element being hovered (for opacity feedback)
  const hoveredViewLayerIdRef = useRef<string | null>(null);

  // Fixed rotation for the current stroke (set once at mousedown, stays constant until mouseup)
  const strokeRotationRef = useRef<number>(0);

  // Preview rotation for random rotation mode (changes over time to indicate randomness)
  const [previewRotation, setPreviewRotation] = useState<number>(0);

  // Measurement tool state
  const rulerStartRef = useRef<Point | null>(null);
  const rulerEndRef = useRef<Point | null>(null);
  const isRulerActiveRef = useRef<boolean>(false);
  const tapePointsRef = useRef<Point[]>([]);
  const tapeTempPointRef = useRef<Point | null>(null);

  // Prop dragging state
  const isDraggingPropRef = useRef<boolean>(false);
  const dragStartPosRef = useRef<Point | null>(null);

  // Prop handle manipulation state
  const activeHandleRef = useRef<PropHandle | null>(null);
  const handleStartPosRef = useRef<Point | null>(null);
  const handleStartScaleRef = useRef<number>(1);
  const handleStartScaleXRef = useRef<number>(1);
  const handleStartScaleYRef = useRef<number>(1);
  const handleStartRotationRef = useRef<number>(0);
  const handlePropCenterRef = useRef<Point>({ x: 0, y: 0 });
  const isCtrlHeldRef = useRef<boolean>(false);
  const isShiftHeldRef = useRef<boolean>(false);

  // Selection box state
  const isSelectionBoxActiveRef = useRef<boolean>(false);
  const selectionBoxStartRef = useRef<Point | null>(null);
  const selectionBoxEndRef = useRef<Point | null>(null);
  const mouseDownPosRef = useRef<Point | null>(null);

  // Text dragging state
  const isDraggingTextRef = useRef<boolean>(false);
  const textDragStartPosRef = useRef<Point | null>(null);
  const textDragOriginalPositionsRef = useRef<Map<string, Point>>(new Map());

  // Text handle manipulation state
  const activeTextHandleRef = useRef<TextHandle | null>(null);
  const textHandleStartPosRef = useRef<Point | null>(null);
  const textHandleStartSizeRef = useRef<{ width: number; height: number } | null>(null);
  const textHandleStartRotationRef = useRef<number>(0);
  const textHandleStartCenterRef = useRef<Point | null>(null);

  // Inline text editing state
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [inlineEditValue, setInlineEditValue] = useState<string>('');

  // Pen tool state
  const tempHandleRef = useRef<Point | null>(null);
  const isDraggingPenHandleRef = useRef<boolean>(false);
  const isAltHeldRef = useRef<boolean>(false);
  const penDragStartPosRef = useRef<Point | null>(null);
  const isDraggingPenPathRef = useRef<boolean>(false);
  const penPathDragStartRef = useRef<Map<string, Point>>(new Map());
  // Anchor/handle manipulation for selected paths
  const editingAnchorRef = useRef<{ pathId: string; pointIndex: number } | null>(null);
  const editingHandleRef = useRef<{ pathId: string; pointIndex: number; handleType: 'in' | 'out' } | null>(null);
  const originalPointRef = useRef<AnchorPoint | null>(null);
  // Multi-anchor dragging state
  const isDraggingMultiAnchorsRef = useRef<boolean>(false);
  const multiAnchorOriginalPositionsRef = useRef<Map<string, Map<number, { x: number; y: number }>>>(new Map());
  // Inserted point handle creation state (Alt+Click+Drag to insert point with handles)
  const isCreatingInsertedPointHandleRef = useRef<boolean>(false);
  const insertedPointInfoRef = useRef<{ pathId: string; pointIndex: number } | null>(null);
  // Pen path rotation
  const isRotatingPenPathRef = useRef<boolean>(false);
  const penPathRotationStartAngleRef = useRef<number>(0);
  const penPathOriginalPointsRef = useRef<Map<string, AnchorPoint[]>>(new Map());
  const penPathCenterRef = useRef<Point | null>(null);

  // Shape tool state
  const shapeDrawStartRef = useRef<Point | null>(null);
  const shapeDrawCurrentRef = useRef<Point | null>(null);
  const isDraggingShapeRef = useRef<boolean>(false);
  const shapeDragStartRef = useRef<Point | null>(null);
  const shapeDragOriginalPosRef = useRef<Map<string, Point>>(new Map());
  const activeShapeHandleRef = useRef<ShapeHandle | null>(null);
  const shapeHandleStartRef = useRef<Point | null>(null);
  const shapeOriginalParamsRef = useRef<ShapeParams | null>(null);
  const shapeOriginalPosRef = useRef<Point | null>(null);
  const isRotatingShapeRef = useRef<boolean>(false);
  const shapeRotationStartAngleRef = useRef<number>(0);
  const shapeOriginalRotationRef = useRef<number>(0);
  const shapeCenterRef = useRef<Point | null>(null);
  // Corner radius handle state
  const isDraggingCornerRadiusRef = useRef<boolean>(false);
  const cornerRadiusStartValueRef = useRef<number>(0);
  const cornerRadiusStartPosRef = useRef<Point | null>(null);
  // Star inner radius handle state
  const isDraggingStarInnerRef = useRef<boolean>(false);
  const starInnerStartRatioRef = useRef<number>(0.5);
  const starInnerStartPosRef = useRef<Point | null>(null);

  // Touch interaction state for mobile support
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastTouchDistanceRef = useRef<number | null>(null);
  const lastTouchCenterRef = useRef<{ x: number; y: number } | null>(null);
  const isTouchPanningRef = useRef<boolean>(false);
  const isPinchZoomingRef = useRef<boolean>(false);

  // Initialize inline edit value when editingTextId changes
  useEffect(() => {
    if (editingTextId) {
      const text = texts.find(t => t.id === editingTextId);
      if (text) {
        setInlineEditValue(text.content);
      }
    }
  }, [editingTextId, texts]);

  // Pending prop click state (for distinguishing click vs drag on unselected props)
  const pendingPropClickRef = useRef<{ prop: PropInstance; screenPos: Point } | null>(null);
  const DRAG_THRESHOLD = 5; // pixels to move before considering it a drag

  // Track if we panned during the current mouse interaction (to distinguish click from pan)
  const didPanRef = useRef<boolean>(false);

  // Multi-prop dragging state (store initial positions for all selected props)
  const multiDragStartRef = useRef<Map<string, Point>>(new Map());

  // Multi-prop transform state (for group resize/rotate)
  const isMultiTransformActiveRef = useRef<boolean>(false);
  const multiTransformStartRef = useRef<{
    props: Map<string, { x: number; y: number; scale: number; scaleX: number; scaleY: number; rotation: number }>;
    center: Point;
    worldCenter: Point;
    boundingBox: { width: number; height: number };
    initialDistance: number;
  } | null>(null);

  // Pre-rendered background pattern cache
  const bgPatternRef = useRef<{
    canvas: HTMLCanvasElement | null;
    material: string | null;
    randomRotation: boolean;
    mapId: string | null;
    averageColor: string | null;
  }>({ canvas: null, material: null, randomRotation: false, mapId: null, averageColor: null });

  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  /**
   * Clear measurement state when tool changes
   */
  useEffect(() => {
    rulerStartRef.current = null;
    rulerEndRef.current = null;
    isRulerActiveRef.current = false;
    tapePointsRef.current = [];
    tapeTempPointRef.current = null;
  }, [activeMeasureTool]);

  /**
   * Update preview rotation for stroke mode (shows random rotation in preview)
   * For continuous mode during drawing, rotation updates are handled in the drawing interval
   */
  useEffect(() => {
    // Generate initial random preview rotation for stroke mode
    if (brushSettings.rotationMode === 'stroke') {
      setPreviewRotation(Math.random() * 360);
    }
  }, [brushSettings.rotationMode]);

  /**
   * Continuous rotation interval for 'continuous' mode while drawing
   */
  const continuousRotationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Calculate distance between two points in meters
   */
  const calculateDistance = useCallback((p1: Point, p2: Point): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const pixelDistance = Math.sqrt(dx * dx + dy * dy);
    return pixelDistance / scale;
  }, [scale]);

  /**
   * Calculate total distance of a polyline
   */
  const calculatePolylineDistance = useCallback((points: Point[]): number => {
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      total += calculateDistance(points[i - 1], points[i]);
    }
    return total;
  }, [calculateDistance]);

  /**
   * Format distance for display
   * Uses m for small distances, km for larger ones
   */
  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${meters.toFixed(2)} m`;
  };

  /**
   * Converts world coordinates to screen coordinates
   */
  const worldToScreen = useCallback((worldX: number, worldY: number): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    return {
      x: (worldX - viewportRef.current.x) * viewportRef.current.zoom + centerX,
      y: (worldY - viewportRef.current.y) * viewportRef.current.zoom + centerY
    };
  }, []);

  /**
   * Find prop at a given world position (only props on active layer are interactive)
   * Sorting uses layer order then zIndex within layer (reverse for top-to-bottom hit testing)
   */
  const findPropAtPosition = useCallback((worldX: number, worldY: number): PropInstance | null => {
    if (!props.length || !propImages) return null;

    // Create layer order map
    const layerOrderMap = new Map<string, number>();
    for (const layer of layers) {
      layerOrderMap.set(layer.id, layer.order);
    }
    if (!layerOrderMap.has('default')) {
      layerOrderMap.set('default', -1);
    }

    // Sort by layer order (descending) then zIndex within layer (descending) for top-to-bottom hit testing
    const sortedProps = [...props].sort((a, b) => {
      const layerOrderA = layerOrderMap.get(a.layerId || 'default') ?? 0;
      const layerOrderB = layerOrderMap.get(b.layerId || 'default') ?? 0;
      if (layerOrderA !== layerOrderB) {
        return layerOrderB - layerOrderA; // Descending
      }
      return (b.zIndex || 0) - (a.zIndex || 0); // Descending
    });

    for (const prop of sortedProps) {
      // Only find props on the active layer
      const propLayerId = prop.layerId || 'default';
      if (propLayerId !== activeLayerId) continue;

      const img = propImages.get(prop.propName);
      if (!img) continue;

      // Use scaleX/scaleY if available, with absolute values for dimensions
      const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
      const scaleX = Math.abs(propAny.scaleX ?? prop.scale);
      const scaleY = Math.abs(propAny.scaleY ?? prop.scale);
      const halfWidth = (img.width * scaleX) / 2;
      const halfHeight = (img.height * scaleY) / 2;

      // Simple bounding box check
      const minX = prop.x - halfWidth;
      const maxX = prop.x + halfWidth;
      const minY = prop.y - halfHeight;
      const maxY = prop.y + halfHeight;

      if (worldX >= minX && worldX <= maxX && worldY >= minY && worldY <= maxY) {
        return prop;
      }
    }

    return null;
  }, [props, propImages, activeLayerId, layers]);

  /**
   * Find text at a given world position (only texts on active layer)
   * Sorting uses layer order then zIndex within layer (reverse for top-to-bottom hit testing)
   */
  const findTextAtPosition = useCallback((worldX: number, worldY: number): TextInstance | null => {
    if (!texts.length) return null;

    // Create layer order map
    const layerOrderMap = new Map<string, number>();
    for (const layer of layers) {
      layerOrderMap.set(layer.id, layer.order);
    }
    if (!layerOrderMap.has('default')) {
      layerOrderMap.set('default', -1);
    }

    // Sort by layer order (descending) then zIndex within layer (descending) for top-to-bottom hit testing
    const sortedTexts = [...texts].sort((a, b) => {
      const layerOrderA = layerOrderMap.get(a.layerId || 'default') ?? 0;
      const layerOrderB = layerOrderMap.get(b.layerId || 'default') ?? 0;
      if (layerOrderA !== layerOrderB) {
        return layerOrderB - layerOrderA; // Descending
      }
      return (b.zIndex || 0) - (a.zIndex || 0); // Descending
    });

    for (const text of sortedTexts) {
      // Only find texts on the active layer
      const textLayerId = text.layerId || 'default';
      if (textLayerId !== activeLayerId) continue;

      const halfWidth = text.width / 2;
      const halfHeight = text.height / 2;

      // Simple bounding box check (doesn't account for rotation)
      const minX = text.x - halfWidth;
      const maxX = text.x + halfWidth;
      const minY = text.y - halfHeight;
      const maxY = text.y + halfHeight;

      if (worldX >= minX && worldX <= maxX && worldY >= minY && worldY <= maxY) {
        return text;
      }
    }

    return null;
  }, [texts, activeLayerId, layers]);

  /**
   * Find the layerId of the topmost element with a description at a given world position
   * Used for view mode hover feedback - searches all visible layers
   * Returns null if no element with description is found
   * Uses precise hit detection: pixel-perfect for props, stroke distance for paths
   */
  const findViewModeHoveredLayerId = useCallback((worldX: number, worldY: number): string | null => {
    // Build layer order map for sorting (higher order = rendered on top)
    const layerOrderMap = new Map<string, number>();
    const layerDescriptionMap = new Map<string, boolean>();

    // Helper to check if a layer or any of its ancestors has a description
    const layerHasDescription = (layerId: string): boolean => {
      if (layerDescriptionMap.has(layerId)) {
        return layerDescriptionMap.get(layerId)!;
      }
      let currentId: string | null = layerId;
      while (currentId) {
        const layer = layers.find(l => l.id === currentId);
        if (!layer) break;
        if (layer.description && layer.description.trim().length > 0) {
          layerDescriptionMap.set(layerId, true);
          return true;
        }
        currentId = layer.parentId;
      }
      layerDescriptionMap.set(layerId, false);
      return false;
    };

    for (const layer of layers) {
      if (layer.visible) {
        layerOrderMap.set(layer.id, layer.order);
      }
    }
    layerOrderMap.set('default', -1);

    type ClickableElement = {
      layerId: string;
      layerOrder: number;
      zIndex: number;
    };
    const hoveredElements: ClickableElement[] = [];

    // Helper to transform position to local space (accounting for rotation)
    const transformToLocal = (clickX: number, clickY: number, centerX: number, centerY: number, rotation: number): { x: number; y: number } => {
      const dx = clickX - centerX;
      const dy = clickY - centerY;
      const rad = -(rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      return {
        x: dx * cos - dy * sin,
        y: dx * sin + dy * cos
      };
    };

    // Helper to check if a point is within a prop's visible pixels (alpha > 0)
    const isPointInPropPixels = (prop: PropInstance, img: HTMLImageElement, localX: number, localY: number, scaleX: number, scaleY: number): boolean => {
      // Convert local coordinates to image pixel coordinates
      const halfWidth = (img.width * scaleX) / 2;
      const halfHeight = (img.height * scaleY) / 2;

      // Check bounding box first
      if (Math.abs(localX) > halfWidth || Math.abs(localY) > halfHeight) {
        return false;
      }

      // Map to image pixel coordinates (0 to width, 0 to height)
      const imgX = Math.floor((localX + halfWidth) / scaleX);
      const imgY = Math.floor((localY + halfHeight) / scaleY);

      // Bounds check
      if (imgX < 0 || imgX >= img.width || imgY < 0 || imgY >= img.height) {
        return false;
      }

      // Try to get pixel data from the image
      try {
        // Create a small canvas to sample the pixel
        const sampleCanvas = document.createElement('canvas');
        sampleCanvas.width = 1;
        sampleCanvas.height = 1;
        const sampleCtx = sampleCanvas.getContext('2d');
        if (!sampleCtx) return true; // Fallback to bounding box

        // Draw just the pixel we need
        sampleCtx.drawImage(img, imgX, imgY, 1, 1, 0, 0, 1, 1);
        const pixelData = sampleCtx.getImageData(0, 0, 1, 1).data;

        // Check alpha channel (index 3)
        return pixelData[3] > 10; // Consider visible if alpha > 10
      } catch {
        // If we can't access pixel data (CORS), fall back to bounding box
        return true;
      }
    };

    // Helper to calculate distance from point to a cubic bezier curve segment
    const distanceToCubicBezier = (
      px: number, py: number,
      x0: number, y0: number,
      cx1: number, cy1: number,
      cx2: number, cy2: number,
      x1: number, y1: number
    ): number => {
      // Sample the bezier curve and find minimum distance
      let minDist = Infinity;
      const steps = 20;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const t2 = t * t;
        const t3 = t2 * t;
        const mt = 1 - t;
        const mt2 = mt * mt;
        const mt3 = mt2 * mt;

        const bx = mt3 * x0 + 3 * mt2 * t * cx1 + 3 * mt * t2 * cx2 + t3 * x1;
        const by = mt3 * y0 + 3 * mt2 * t * cy1 + 3 * mt * t2 * cy2 + t3 * y1;

        const dist = Math.sqrt((px - bx) * (px - bx) + (py - by) * (py - by));
        minDist = Math.min(minDist, dist);
      }
      return minDist;
    };

    // Helper to calculate distance from point to a line segment
    const distanceToLineSegment = (px: number, py: number, x0: number, y0: number, x1: number, y1: number): number => {
      const dx = x1 - x0;
      const dy = y1 - y0;
      const lenSq = dx * dx + dy * dy;

      if (lenSq === 0) {
        return Math.sqrt((px - x0) * (px - x0) + (py - y0) * (py - y0));
      }

      let t = ((px - x0) * dx + (py - y0) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));

      const nearestX = x0 + t * dx;
      const nearestY = y0 + t * dy;

      return Math.sqrt((px - nearestX) * (px - nearestX) + (py - nearestY) * (py - nearestY));
    };

    // Helper to check if point is within stroke distance of a pen path
    const isPointOnPenPath = (path: PenPathInstance, px: number, py: number): boolean => {
      const points = path.points;
      if (points.length === 0) return false;

      const strokeRadius = (path.strokeWidth || 2) / 2 + 2; // Add small tolerance

      // Check distance to first point
      if (points.length === 1) {
        const dist = Math.sqrt((px - points[0].x) ** 2 + (py - points[0].y) ** 2);
        return dist <= strokeRadius;
      }

      // Check each segment
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];

        // Determine if this is a bezier curve or straight line
        const hasHandles = p0.handleOut || p1.handleIn;

        let dist: number;
        if (hasHandles) {
          // Cubic bezier curve
          const cx1 = p0.x + (p0.handleOut?.x || 0);
          const cy1 = p0.y + (p0.handleOut?.y || 0);
          const cx2 = p1.x + (p1.handleIn?.x || 0);
          const cy2 = p1.y + (p1.handleIn?.y || 0);
          dist = distanceToCubicBezier(px, py, p0.x, p0.y, cx1, cy1, cx2, cy2, p1.x, p1.y);
        } else {
          // Straight line
          dist = distanceToLineSegment(px, py, p0.x, p0.y, p1.x, p1.y);
        }

        if (dist <= strokeRadius) {
          return true;
        }
      }

      // For closed paths, check the closing segment
      if (path.closed && points.length > 2) {
        const p0 = points[points.length - 1];
        const p1 = points[0];
        const hasHandles = p0.handleOut || p1.handleIn;

        let dist: number;
        if (hasHandles) {
          const cx1 = p0.x + (p0.handleOut?.x || 0);
          const cy1 = p0.y + (p0.handleOut?.y || 0);
          const cx2 = p1.x + (p1.handleIn?.x || 0);
          const cy2 = p1.y + (p1.handleIn?.y || 0);
          dist = distanceToCubicBezier(px, py, p0.x, p0.y, cx1, cy1, cx2, cy2, p1.x, p1.y);
        } else {
          dist = distanceToLineSegment(px, py, p0.x, p0.y, p1.x, p1.y);
        }

        if (dist <= strokeRadius) {
          return true;
        }
      }

      return false;
    };

    // Helper to check if point is inside a polygon (for filled shapes)
    const isPointInPolygon = (px: number, py: number, vertices: { x: number; y: number }[]): boolean => {
      let inside = false;
      for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
        const xi = vertices[i].x, yi = vertices[i].y;
        const xj = vertices[j].x, yj = vertices[j].y;
        if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
          inside = !inside;
        }
      }
      return inside;
    };

    // Check props with pixel-perfect detection
    for (const prop of props) {
      const propLayerId = prop.layerId || 'default';
      if (!layerOrderMap.has(propLayerId)) continue;

      const img = propImages?.get(prop.propName);
      if (!img) continue;

      const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
      const scaleX = Math.abs(propAny.scaleX ?? prop.scale);
      const scaleY = Math.abs(propAny.scaleY ?? prop.scale);

      const local = transformToLocal(worldX, worldY, prop.x, prop.y, prop.rotation || 0);

      if (isPointInPropPixels(prop, img, local.x, local.y, scaleX, scaleY)) {
        hoveredElements.push({
          layerId: propLayerId,
          layerOrder: layerOrderMap.get(propLayerId) ?? 0,
          zIndex: prop.zIndex || 0
        });
      }
    }

    // Check texts with precise text shape detection
    for (const text of texts) {
      const textLayerId = text.layerId || 'default';
      if (!layerOrderMap.has(textLayerId)) continue;

      const currentZoom = viewportRef.current.zoom;
      const local = transformToLocal(worldX, worldY, text.x, text.y, text.rotation || 0);

      // Calculate dimensions based on fixedSize mode
      // For hit detection, we work in "text local space" where the text is centered at origin
      const boxWidth = text.width || 100;
      const boxHeight = text.height || 20;
      const fontSize = boxHeight * 0.7;
      const lineHeight = fontSize * 1.2;
      const padding = 4;

      // Scale factor: for fixedSize, we need to convert world coords to screen-like coords
      const scale = text.fixedSize ? currentZoom : 1;
      const localX = local.x * scale;
      const localY = local.y * scale;

      // Quick bounding box check first
      if (Math.abs(localX) > boxWidth / 2 || Math.abs(localY) > boxHeight / 2) {
        continue;
      }

      // Create a temporary canvas to measure text
      const measureCanvas = document.createElement('canvas');
      const measureCtx = measureCanvas.getContext('2d');
      if (!measureCtx) {
        // Fallback to bounding box
        hoveredElements.push({
          layerId: textLayerId,
          layerOrder: layerOrderMap.get(textLayerId) ?? 0,
          zIndex: text.zIndex || 0
        });
        continue;
      }

      measureCtx.font = `${text.fontWeight} ${fontSize}px ${text.fontFamily}`;

      // Word wrap like in rendering
      const maxWidth = boxWidth - padding * 2;
      const words = text.content.split(/(\s+)/);
      const lines: { text: string; width: number }[] = [];
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine + word;
        const testWidth = measureCtx.measureText(testLine).width;

        if (testWidth > maxWidth && currentLine !== '') {
          const trimmed = currentLine.trimEnd();
          lines.push({ text: trimmed, width: measureCtx.measureText(trimmed).width });
          currentLine = word.trimStart();
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) {
        const trimmed = currentLine.trimEnd();
        lines.push({ text: trimmed, width: measureCtx.measureText(trimmed).width });
      }

      if (lines.length === 0) {
        continue; // Empty text, nothing to hit
      }

      // Calculate text bounds
      const totalTextHeight = lines.length * lineHeight;
      const startY = -boxHeight / 2 + padding;

      // Check if point is within any line of text
      let isHit = false;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineY = startY + i * lineHeight;

        // Calculate line X based on alignment
        let lineStartX: number;
        if (text.alignment === 'center') {
          lineStartX = -line.width / 2;
        } else if (text.alignment === 'right') {
          lineStartX = boxWidth / 2 - padding - line.width;
        } else {
          lineStartX = -boxWidth / 2 + padding;
        }

        // Check if point is within this line (with some vertical padding for line height)
        if (localX >= lineStartX && localX <= lineStartX + line.width &&
            localY >= lineY && localY <= lineY + lineHeight) {
          isHit = true;
          break;
        }
      }

      if (isHit) {
        hoveredElements.push({
          layerId: textLayerId,
          layerOrder: layerOrderMap.get(textLayerId) ?? 0,
          zIndex: text.zIndex || 0
        });
      }
    }

    // Check pen paths with precise stroke detection
    for (const path of penPaths) {
      const pathLayerId = path.layerId || 'default';
      if (!layerOrderMap.has(pathLayerId)) continue;

      if (isPointOnPenPath(path, worldX, worldY)) {
        hoveredElements.push({
          layerId: pathLayerId,
          layerOrder: layerOrderMap.get(pathLayerId) ?? 0,
          zIndex: path.zIndex || 0
        });
      }
    }

    // Check shapes with precise detection
    for (const shape of shapes) {
      const shapeLayerId = shape.layerId || 'default';
      if (!layerOrderMap.has(shapeLayerId)) continue;

      const local = transformToLocal(worldX, worldY, shape.x, shape.y, shape.rotation || 0);
      let isHit = false;

      if (shape.shapeType === 'ellipse') {
        const params = shape.shapeParams.params as import('../../types/shape').EllipseParams;
        const rx = params.radiusX * shape.scale;
        const ry = params.radiusY * shape.scale;
        isHit = (local.x * local.x) / (rx * rx) + (local.y * local.y) / (ry * ry) <= 1;
      } else if (shape.shapeType === 'rectangle') {
        const params = shape.shapeParams.params as import('../../types/shape').RectangleParams;
        const hw = (params.width * shape.scale) / 2;
        const hh = (params.height * shape.scale) / 2;
        const cr = (params.cornerRadius || 0) * shape.scale;

        if (cr > 0) {
          // Rectangle with rounded corners
          const innerHw = hw - cr;
          const innerHh = hh - cr;

          // Check if in the main cross area
          if ((Math.abs(local.x) <= innerHw && Math.abs(local.y) <= hh) ||
              (Math.abs(local.x) <= hw && Math.abs(local.y) <= innerHh)) {
            isHit = true;
          } else {
            // Check corners (quarter circles)
            const cornerX = Math.abs(local.x) - innerHw;
            const cornerY = Math.abs(local.y) - innerHh;
            if (cornerX > 0 && cornerY > 0) {
              isHit = (cornerX * cornerX + cornerY * cornerY) <= (cr * cr);
            }
          }
        } else {
          isHit = Math.abs(local.x) <= hw && Math.abs(local.y) <= hh;
        }
      } else if (shape.shapeType === 'polygon') {
        const params = shape.shapeParams.params as import('../../types/shape').PolygonParams;
        const radius = params.radius * shape.scale;
        const sides = params.sides;

        // Generate polygon vertices
        const vertices: { x: number; y: number }[] = [];
        for (let i = 0; i < sides; i++) {
          const angle = (i * 2 * Math.PI / sides) - Math.PI / 2;
          vertices.push({
            x: radius * Math.cos(angle),
            y: radius * Math.sin(angle)
          });
        }
        isHit = isPointInPolygon(local.x, local.y, vertices);
      } else if (shape.shapeType === 'star') {
        const params = shape.shapeParams.params as import('../../types/shape').StarParams;
        const outerRadius = params.outerRadius * shape.scale;
        const innerRadius = outerRadius * params.innerRatio;
        const points = params.points;

        // Generate star vertices (alternating outer and inner)
        const vertices: { x: number; y: number }[] = [];
        for (let i = 0; i < points * 2; i++) {
          const angle = (i * Math.PI / points) - Math.PI / 2;
          const r = i % 2 === 0 ? outerRadius : innerRadius;
          vertices.push({
            x: r * Math.cos(angle),
            y: r * Math.sin(angle)
          });
        }
        isHit = isPointInPolygon(local.x, local.y, vertices);
      } else {
        // Fallback for unknown shapes
        const size = 50 * shape.scale;
        isHit = Math.abs(local.x) <= size && Math.abs(local.y) <= size;
      }

      if (isHit) {
        hoveredElements.push({
          layerId: shapeLayerId,
          layerOrder: layerOrderMap.get(shapeLayerId) ?? 0,
          zIndex: shape.zIndex || 0
        });
      }
    }

    // Sort by layer order (desc) then zIndex (desc)
    if (hoveredElements.length > 0) {
      hoveredElements.sort((a, b) => {
        if (a.layerOrder !== b.layerOrder) {
          return b.layerOrder - a.layerOrder;
        }
        return b.zIndex - a.zIndex;
      });

      // Find the first element whose layer has a description
      for (const element of hoveredElements) {
        if (layerHasDescription(element.layerId)) {
          return element.layerId;
        }
      }
    }

    return null;
  }, [layers, props, propImages, texts, penPaths, shapes]);

  /**
   * Find pen path at a given world position (only paths on active layer)
   * Uses bounding box check for simplicity
   * Sorting uses layer order then zIndex within layer (reverse for top-to-bottom hit testing)
   */
  const findPenPathAtPosition = useCallback((worldX: number, worldY: number): PenPathInstance | null => {
    if (!penPaths.length) return null;

    // Create layer order map
    const layerOrderMap = new Map<string, number>();
    for (const layer of layers) {
      layerOrderMap.set(layer.id, layer.order);
    }
    if (!layerOrderMap.has('default')) {
      layerOrderMap.set('default', -1);
    }

    // Sort by layer order (descending) then zIndex within layer (descending) for top-to-bottom hit testing
    const sortedPaths = [...penPaths].sort((a, b) => {
      const layerOrderA = layerOrderMap.get(a.layerId || 'default') ?? 0;
      const layerOrderB = layerOrderMap.get(b.layerId || 'default') ?? 0;
      if (layerOrderA !== layerOrderB) {
        return layerOrderB - layerOrderA; // Descending
      }
      return (b.zIndex || 0) - (a.zIndex || 0); // Descending
    });

    for (const path of sortedPaths) {
      // Only find paths on the active layer
      const pathLayerId = path.layerId || 'default';
      if (pathLayerId !== activeLayerId) continue;

      if (path.points.length === 0) continue;

      // Calculate bounding box from points
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const point of path.points) {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
      }

      // Add some padding based on stroke width
      const padding = (path.strokeWidth || 2) / 2 + 5;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      if (worldX >= minX && worldX <= maxX && worldY >= minY && worldY <= maxY) {
        return path;
      }
    }

    return null;
  }, [penPaths, activeLayerId, layers]);

  /**
   * Find an anchor point or handle at a given world position for selected paths
   * Returns { pathId, pointIndex, type: 'anchor' | 'handleIn' | 'handleOut' } or null
   */
  const findAnchorOrHandleAtPosition = useCallback((worldX: number, worldY: number): {
    pathId: string;
    pointIndex: number;
    type: 'anchor' | 'handleIn' | 'handleOut';
  } | null => {
    if (selectedPenPathIds.length === 0) return null;

    const hitRadius = 8 / viewport.zoom; // 8 pixels tolerance

    for (const pathId of selectedPenPathIds) {
      const path = penPaths.find(p => p.id === pathId);
      if (!path) continue;

      for (let i = 0; i < path.points.length; i++) {
        const point = path.points[i];

        // Check handles first (they should have priority when overlapping anchor)
        if (point.handleIn) {
          const handleX = point.x + point.handleIn.x;
          const handleY = point.y + point.handleIn.y;
          const dist = Math.sqrt((worldX - handleX) ** 2 + (worldY - handleY) ** 2);
          if (dist <= hitRadius) {
            return { pathId, pointIndex: i, type: 'handleIn' };
          }
        }

        if (point.handleOut) {
          const handleX = point.x + point.handleOut.x;
          const handleY = point.y + point.handleOut.y;
          const dist = Math.sqrt((worldX - handleX) ** 2 + (worldY - handleY) ** 2);
          if (dist <= hitRadius) {
            return { pathId, pointIndex: i, type: 'handleOut' };
          }
        }

        // Check anchor point
        const anchorDist = Math.sqrt((worldX - point.x) ** 2 + (worldY - point.y) ** 2);
        if (anchorDist <= hitRadius) {
          return { pathId, pointIndex: i, type: 'anchor' };
        }
      }
    }

    return null;
  }, [selectedPenPathIds, penPaths, viewport.zoom]);

  /**
   * Find if clicking on a path segment (bezier curve between two anchor points)
   * Returns { pathId, segmentIndex, t, point } where t is the curve parameter (0-1) and point is the world position
   */
  const findPathSegmentAtPosition = useCallback((worldX: number, worldY: number): {
    pathId: string;
    segmentIndex: number;
    t: number;
    point: { x: number; y: number };
  } | null => {
    if (selectedPenPathIds.length === 0) return null;

    const hitRadius = 8 / viewport.zoom;

    for (const pathId of selectedPenPathIds) {
      const path = penPaths.find(p => p.id === pathId);
      if (!path || path.points.length < 2) continue;

      const numSegments = path.closed ? path.points.length : path.points.length - 1;

      for (let i = 0; i < numSegments; i++) {
        const p0 = path.points[i];
        const p1 = path.points[(i + 1) % path.points.length];

        // Get control points for cubic bezier
        const cp0x = p0.handleOut ? p0.x + p0.handleOut.x : p0.x;
        const cp0y = p0.handleOut ? p0.y + p0.handleOut.y : p0.y;
        const cp1x = p1.handleIn ? p1.x + p1.handleIn.x : p1.x;
        const cp1y = p1.handleIn ? p1.y + p1.handleIn.y : p1.y;

        // Sample the bezier curve and find closest point
        const samples = 20;
        let closestT = 0;
        let closestDist = Infinity;
        let closestPoint = { x: p0.x, y: p0.y };

        for (let s = 0; s <= samples; s++) {
          const t = s / samples;
          const mt = 1 - t;
          // Cubic bezier formula
          const x = mt * mt * mt * p0.x + 3 * mt * mt * t * cp0x + 3 * mt * t * t * cp1x + t * t * t * p1.x;
          const y = mt * mt * mt * p0.y + 3 * mt * mt * t * cp0y + 3 * mt * t * t * cp1y + t * t * t * p1.y;

          const dist = Math.sqrt((worldX - x) ** 2 + (worldY - y) ** 2);
          if (dist < closestDist) {
            closestDist = dist;
            closestT = t;
            closestPoint = { x, y };
          }
        }

        if (closestDist <= hitRadius) {
          return { pathId, segmentIndex: i, t: closestT, point: closestPoint };
        }
      }
    }

    return null;
  }, [selectedPenPathIds, penPaths, viewport.zoom]);

  /**
   * Find if clicking on the rotation handle for selected pen paths
   * Returns true if clicking on rotation handle, false otherwise
   */
  const findPenPathRotationHandleAtPosition = useCallback((screenX: number, screenY: number): boolean => {
    if (selectedPenPathIds.length === 0) return false;

    // Calculate combined bounding box for all selected paths
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const pathId of selectedPenPathIds) {
      const path = penPaths.find(p => p.id === pathId);
      if (!path) continue;

      for (const point of path.points) {
        const sx = (point.x - viewport.x) * viewport.zoom + (canvasRef.current?.width ?? 0) / 2;
        const sy = (point.y - viewport.y) * viewport.zoom + (canvasRef.current?.height ?? 0) / 2;
        minX = Math.min(minX, sx);
        minY = Math.min(minY, sy);
        maxX = Math.max(maxX, sx);
        maxY = Math.max(maxY, sy);

        // Include handles
        if (point.handleIn) {
          const hx = ((point.x + point.handleIn.x) - viewport.x) * viewport.zoom + (canvasRef.current?.width ?? 0) / 2;
          const hy = ((point.y + point.handleIn.y) - viewport.y) * viewport.zoom + (canvasRef.current?.height ?? 0) / 2;
          minX = Math.min(minX, hx);
          minY = Math.min(minY, hy);
          maxX = Math.max(maxX, hx);
          maxY = Math.max(maxY, hy);
        }
        if (point.handleOut) {
          const hx = ((point.x + point.handleOut.x) - viewport.x) * viewport.zoom + (canvasRef.current?.width ?? 0) / 2;
          const hy = ((point.y + point.handleOut.y) - viewport.y) * viewport.zoom + (canvasRef.current?.height ?? 0) / 2;
          minX = Math.min(minX, hx);
          minY = Math.min(minY, hy);
          maxX = Math.max(maxX, hx);
          maxY = Math.max(maxY, hy);
        }
      }
    }

    if (minX === Infinity) return false;

    // Calculate rotation handle position (same as in penPathRenderer.ts)
    const centerX = (minX + maxX) / 2;
    const handleDistance = 25;
    const handleY = minY - handleDistance;
    const handleRadius = 6;

    // Check if click is within the rotation handle
    const dist = Math.sqrt((screenX - centerX) ** 2 + (screenY - handleY) ** 2);
    return dist <= handleRadius;
  }, [selectedPenPathIds, penPaths, viewport]);

  /**
   * Find all props within a rectangular area (world coordinates, only props on active layer)
   */
  const findPropsInRect = useCallback((x1: number, y1: number, x2: number, y2: number): PropInstance[] => {
    if (!props.length || !propImages) return [];

    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const result: PropInstance[] = [];

    for (const prop of props) {
      // Only find props on the active layer
      const propLayerId = prop.layerId || 'default';
      if (propLayerId !== activeLayerId) continue;

      const img = propImages.get(prop.propName);
      if (!img) continue;

      // Use absolute values for dimensions since negative scale is for flipping
      const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
      const scaleX = Math.abs(propAny.scaleX ?? prop.scale);
      const scaleY = Math.abs(propAny.scaleY ?? prop.scale);
      const halfWidth = (img.width * scaleX) / 2;
      const halfHeight = (img.height * scaleY) / 2;

      // Check if prop bounding box intersects with selection box
      const propMinX = prop.x - halfWidth;
      const propMaxX = prop.x + halfWidth;
      const propMinY = prop.y - halfHeight;
      const propMaxY = prop.y + halfHeight;

      if (propMaxX >= minX && propMinX <= maxX && propMaxY >= minY && propMinY <= maxY) {
        result.push(prop);
      }
    }

    return result;
  }, [props, propImages, activeLayerId]);

  /**
   * Get bounding box and handle positions for multiple selected props (in screen coordinates)
   */
  const getMultiSelectionBounds = useCallback((): {
    center: Point;
    handles: { [key in PropHandle]: Point };
    worldBounds: { minX: number; minY: number; maxX: number; maxY: number };
  } | null => {
    if (selectedPropIds.length < 2 || !propImages) return null;

    const selectedProps = props.filter(p => selectedPropIds.includes(p.id));
    if (selectedProps.length < 2) return null;

    // Calculate world-space bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const prop of selectedProps) {
      const img = propImages.get(prop.propName);
      if (!img) continue;

      const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
      const scaleX = Math.abs(propAny.scaleX ?? prop.scale);
      const scaleY = Math.abs(propAny.scaleY ?? prop.scale);
      const halfWidth = (img.width * scaleX) / 2;
      const halfHeight = (img.height * scaleY) / 2;

      // Simple AABB (axis-aligned bounding box) - doesn't account for rotation perfectly
      // but good enough for selection purposes
      minX = Math.min(minX, prop.x - halfWidth);
      maxX = Math.max(maxX, prop.x + halfWidth);
      minY = Math.min(minY, prop.y - halfHeight);
      maxY = Math.max(maxY, prop.y + halfHeight);
    }

    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // World center of selection
    const worldCenterX = (minX + maxX) / 2;
    const worldCenterY = (minY + maxY) / 2;

    // Convert to screen coordinates
    const screenCenterX = (worldCenterX - viewportRef.current.x) * viewportRef.current.zoom + centerX;
    const screenCenterY = (worldCenterY - viewportRef.current.y) * viewportRef.current.zoom + centerY;

    const halfWidth = ((maxX - minX) / 2) * viewportRef.current.zoom;
    const halfHeight = ((maxY - minY) / 2) * viewportRef.current.zoom;

    // Corner positions (no rotation for multi-selection)
    const handles: { [key in PropHandle]: Point } = {
      tl: { x: screenCenterX - halfWidth, y: screenCenterY - halfHeight },
      tr: { x: screenCenterX + halfWidth, y: screenCenterY - halfHeight },
      bl: { x: screenCenterX - halfWidth, y: screenCenterY + halfHeight },
      br: { x: screenCenterX + halfWidth, y: screenCenterY + halfHeight },
      rotate: { x: screenCenterX, y: screenCenterY - halfHeight - 30 }
    };

    return {
      center: { x: screenCenterX, y: screenCenterY },
      handles,
      worldBounds: { minX, minY, maxX, maxY }
    };
  }, [selectedPropIds, props, propImages]);

  /**
   * Get handle positions for a selected prop (in screen coordinates)
   */
  const getHandlePositions = useCallback((prop: PropInstance): { [key in PropHandle]: Point } | null => {
    if (!propImages) return null;
    const img = propImages.get(prop.propName);
    if (!img) return null;

    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const screenX = (prop.x - viewportRef.current.x) * viewportRef.current.zoom + centerX;
    const screenY = (prop.y - viewportRef.current.y) * viewportRef.current.zoom + centerY;

    // Get the prop's actual scale values (handle scaleX/scaleY if available)
    // Use absolute values for dimensions since negative scale is for flipping
    const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
    const scaleX = propAny.scaleX ?? prop.scale;
    const scaleY = propAny.scaleY ?? prop.scale;

    const halfWidth = (img.width * Math.abs(scaleX) * viewportRef.current.zoom) / 2;
    const halfHeight = (img.height * Math.abs(scaleY) * viewportRef.current.zoom) / 2;

    // Corner positions before rotation
    const corners = {
      tl: { x: -halfWidth, y: -halfHeight },
      tr: { x: halfWidth, y: -halfHeight },
      bl: { x: -halfWidth, y: halfHeight },
      br: { x: halfWidth, y: halfHeight }
    };

    // Apply rotation
    const rad = (prop.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const rotatePoint = (p: Point): Point => ({
      x: screenX + p.x * cos - p.y * sin,
      y: screenY + p.x * sin + p.y * cos
    });

    // Rotation handle is above the prop
    const rotateHandleDistance = Math.max(halfHeight, halfWidth) + 30;
    const rotateOffset = { x: 0, y: -rotateHandleDistance };

    return {
      tl: rotatePoint(corners.tl),
      tr: rotatePoint(corners.tr),
      bl: rotatePoint(corners.bl),
      br: rotatePoint(corners.br),
      rotate: rotatePoint(rotateOffset)
    };
  }, [propImages]);

  /**
   * Find which handle is at a given screen position (single prop)
   */
  const findHandleAtPosition = useCallback((screenX: number, screenY: number, prop: PropInstance): PropHandle | null => {
    const handles = getHandlePositions(prop);
    if (!handles) return null;

    const handleSize = 12; // Size of handle hit area

    for (const [key, pos] of Object.entries(handles)) {
      const dx = screenX - pos.x;
      const dy = screenY - pos.y;
      if (Math.sqrt(dx * dx + dy * dy) <= handleSize) {
        return key as PropHandle;
      }
    }

    return null;
  }, [getHandlePositions]);

  /**
   * Find which handle is at a given screen position (multi-selection)
   */
  const findMultiHandleAtPosition = useCallback((screenX: number, screenY: number): PropHandle | null => {
    const multiBounds = getMultiSelectionBounds();
    if (!multiBounds) return null;

    const handleSize = 12; // Size of handle hit area

    for (const [key, pos] of Object.entries(multiBounds.handles)) {
      const dx = screenX - pos.x;
      const dy = screenY - pos.y;
      if (Math.sqrt(dx * dx + dy * dy) <= handleSize) {
        return key as PropHandle;
      }
    }

    return null;
  }, [getMultiSelectionBounds]);

  /**
   * Get handle positions for a text instance
   */
  const getTextHandlePositions = useCallback((text: TextInstance): { [key in TextHandle]: Point } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const screenX = (text.x - viewportRef.current.x) * viewportRef.current.zoom + centerX;
    const screenY = (text.y - viewportRef.current.y) * viewportRef.current.zoom + centerY;

    // For fixed size mode, dimensions stay constant regardless of zoom
    const halfWidth = text.fixedSize ? text.width / 2 : (text.width * viewportRef.current.zoom) / 2;
    const halfHeight = text.fixedSize ? text.height / 2 : (text.height * viewportRef.current.zoom) / 2;

    // Corner positions before rotation
    const corners = {
      tl: { x: -halfWidth, y: -halfHeight },
      tr: { x: halfWidth, y: -halfHeight },
      bl: { x: -halfWidth, y: halfHeight },
      br: { x: halfWidth, y: halfHeight }
    };

    // Apply rotation
    const rad = (text.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const rotatePoint = (p: Point): Point => ({
      x: screenX + p.x * cos - p.y * sin,
      y: screenY + p.x * sin + p.y * cos
    });

    // Rotation handle is above the text
    const rotateHandleDistance = halfHeight + 30;
    const rotateOffset = { x: 0, y: -rotateHandleDistance };

    return {
      tl: rotatePoint(corners.tl),
      tr: rotatePoint(corners.tr),
      bl: rotatePoint(corners.bl),
      br: rotatePoint(corners.br),
      rotate: rotatePoint(rotateOffset)
    };
  }, []);

  /**
   * Find which text handle is at a given screen position
   */
  const findTextHandleAtPosition = useCallback((screenX: number, screenY: number, text: TextInstance): TextHandle | null => {
    const handles = getTextHandlePositions(text);
    if (!handles) return null;

    const handleSize = 12; // Size of handle hit area

    for (const [key, pos] of Object.entries(handles)) {
      const dx = screenX - pos.x;
      const dy = screenY - pos.y;
      if (Math.sqrt(dx * dx + dy * dy) <= handleSize) {
        return key as TextHandle;
      }
    }

    return null;
  }, [getTextHandlePositions]);

  /**
   * Check if a prop can be placed at the current zoom level
   * Returns true if the prop would be at least 1/100th of the screen width
   * This prevents accidental placement of tiny props when very zoomed out
   */
  const canPlacePropAtZoom = useCallback((): boolean => {
    if (!propSettings?.selectedProp || !propImages) return false;

    const propImg = propImages.get(propSettings.selectedProp);
    if (!propImg) return false;

    const canvas = canvasRef.current;
    if (!canvas) return false;

    const rect = canvas.getBoundingClientRect();
    const screenWidth = rect.width;

    // Prop size in world units
    const propWorldSize = propImg.width * propSettings.scale;

    // Screen width in world units at current zoom
    const screenWorldWidth = screenWidth / viewportRef.current.zoom;

    // Prop must be at least 1/100th of the visible screen width
    const minRatio = 1 / 100;

    return propWorldSize >= screenWorldWidth * minRatio;
  }, [propSettings, propImages]);

  /**
   * Converts screen coordinates to world coordinates
   */
  const screenToWorld = useCallback((screenX: number, screenY: number): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    return {
      x: (screenX - rect.left - centerX) / viewportRef.current.zoom + viewportRef.current.x,
      y: (screenY - rect.top - centerY) / viewportRef.current.zoom + viewportRef.current.y
    };
  }, []);

  /**
   * Draws a brush stroke at the given world position
   * Uses strokeRotationRef which is set once at the start of a stroke
   */
  const drawStroke = useCallback((worldX: number, worldY: number): void => {
    if (!tileManager || !brushSettings.brushName) return;

    if (activeTool === 'eraser') {
      tileManager.eraseBrushStroke(
        worldX,
        worldY,
        brushSettings.brushName,
        brushSettings.size,
        strokeRotationRef.current,
        brushSettings.strength
      );
    } else if (activeTool === 'brush' && brushSettings.materialName) {
      tileManager.drawBrushStroke(
        worldX,
        worldY,
        brushSettings.materialName,
        brushSettings.brushName,
        brushSettings.size,
        strokeRotationRef.current,
        brushSettings.strength
      );
    }

    onDraw?.();
  }, [tileManager, brushSettings, activeTool, onDraw]);

  /**
   * Draws strokes along a line between two points
   */
  const drawStrokesAlongLine = useCallback((
    fromX: number,
    fromY: number,
    toX: number,
    toY: number
  ): void => {
    const spacing = brushSettings.size * STROKE_SPACING_FACTOR;
    const dx = toX - fromX;
    const dy = toY - fromY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < spacing) {
      drawStroke(toX, toY);
      return;
    }

    const steps = Math.ceil(distance / spacing);
    const stepX = dx / steps;
    const stepY = dy / steps;

    for (let i = 1; i <= steps; i++) {
      drawStroke(fromX + stepX * i, fromY + stepY * i);
    }
  }, [brushSettings.size, drawStroke]);

  /**
   * Main render function
   */
  const render = useCallback((): void => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const dpr = window.devicePixelRatio || 1;

    // Resize canvas if needed
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    const centerX = width / 2;
    const centerY = height / 2;

    // Reset transform and scale for DPR
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Clear canvas with background color
    // Use custom backgroundColor if set, otherwise default gray
    const defaultBgColor = backgroundColor || '#1a1a1a';
    ctx.fillStyle = defaultBgColor;
    ctx.fillRect(0, 0, width, height);

    // Zoom threshold: below 5% we use average color only (no pattern, no tiles)
    const MIN_DETAIL_ZOOM = 0.05;
    const useAverageColorOnly = viewport.zoom < MIN_DETAIL_ZOOM;

    // Draw background material (tiled infinitely)
    const bgMaterialImg = backgroundMaterial ? materialImages.get(backgroundMaterial) : null;
    if (bgMaterialImg) {
      // Check if we need to regenerate the background pattern
      const needsRegenerate =
        bgPatternRef.current.canvas === null ||
        bgPatternRef.current.material !== backgroundMaterial ||
        bgPatternRef.current.randomRotation !== backgroundRandomRotation ||
        bgPatternRef.current.mapId !== mapId;

      if (needsRegenerate) {
        const tileW = bgMaterialImg.width;
        const tileH = bgMaterialImg.height;

        let patternCanvas: HTMLCanvasElement;

        if (backgroundRandomRotation) {
          // Random rotation mode: larger pattern with rotated tiles
          const seed = stringToSeed(mapId);
          const random = createSeededRandom(seed);
          const GRID_SIZE = 8;
          const patternSize = tileW * GRID_SIZE;

          patternCanvas = document.createElement('canvas');
          patternCanvas.width = patternSize;
          patternCanvas.height = patternSize;
          const patternCtx = patternCanvas.getContext('2d');

          if (patternCtx) {
            for (let tx = 0; tx < GRID_SIZE; tx++) {
              for (let ty = 0; ty < GRID_SIZE; ty++) {
                const rotation = Math.floor(random() * 4) * 90;
                const cx = tx * tileW + tileW / 2;
                const cy = ty * tileH + tileH / 2;

                patternCtx.save();
                patternCtx.translate(cx, cy);
                if (rotation !== 0) {
                  patternCtx.rotate((rotation * Math.PI) / 180);
                }
                patternCtx.drawImage(bgMaterialImg, -tileW / 2, -tileH / 2, tileW, tileH);
                patternCtx.restore();
              }
            }
          }
        } else {
          // Simple 1:1 pattern
          patternCanvas = document.createElement('canvas');
          patternCanvas.width = tileW;
          patternCanvas.height = tileH;
          const patternCtx = patternCanvas.getContext('2d');
          if (patternCtx) {
            patternCtx.drawImage(bgMaterialImg, 0, 0);
          }
        }

        // Calculate average color from the pattern canvas
        let averageColor = defaultBgColor;
        const avgCtx = patternCanvas.getContext('2d');
        if (avgCtx) {
          const imageData = avgCtx.getImageData(0, 0, patternCanvas.width, patternCanvas.height);
          const data = imageData.data;
          let r = 0, g = 0, b = 0, count = 0;

          // Sample every 4th pixel for performance
          for (let i = 0; i < data.length; i += 16) {
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count++;
          }

          if (count > 0) {
            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);
            averageColor = `rgb(${r}, ${g}, ${b})`;
          }
        }

        bgPatternRef.current = {
          canvas: patternCanvas,
          material: backgroundMaterial,
          randomRotation: backgroundRandomRotation,
          mapId: mapId,
          averageColor: averageColor
        };
      }

      if (useAverageColorOnly) {
        // At very low zoom, just fill with average color
        if (bgPatternRef.current.averageColor) {
          ctx.fillStyle = bgPatternRef.current.averageColor;
          ctx.fillRect(0, 0, width, height);
        }
      } else {
        // Normal zoom: render the pattern
        const patternCanvas = bgPatternRef.current.canvas;
        if (patternCanvas) {
          const worldOffsetX = -viewport.x * viewport.zoom + centerX;
          const worldOffsetY = -viewport.y * viewport.zoom + centerY;

          ctx.save();

          const pattern = ctx.createPattern(patternCanvas, 'repeat');
          if (pattern) {
            const matrix = new DOMMatrix();
            matrix.translateSelf(worldOffsetX, worldOffsetY);
            matrix.scaleSelf(viewport.zoom, viewport.zoom);
            pattern.setTransform(matrix);

            ctx.fillStyle = pattern;
            ctx.fillRect(0, 0, width, height);
          }

          ctx.restore();
        }
      }
    }

    // Render tiles (always render, background optimization only affects the pattern behind)
    if (tileManager) {
      tileManager.renderToCanvas(ctx, viewport.x, viewport.y, centerX, centerY, viewport.zoom);
    }

    // =========================================================================
    // LAYER-BASED RENDERING: Render all items (props, paths, texts) by layer order
    // =========================================================================

    // Build hierarchical render order map
    // This ensures children are always rendered with their parent group,
    // respecting the visual hierarchy shown in the layer panel
    const layerRenderOrderMap = new Map<string, number>();

    // Helper to recursively build render order
    const buildRenderOrder = (parentId: string | null, baseOrder: number): number => {
      // Get children of this parent, sorted by their order (ascending = bottom to top)
      const children = layers
        .filter(l => l.parentId === parentId)
        .sort((a, b) => a.order - b.order);

      let currentOrder = baseOrder;
      for (const child of children) {
        layerRenderOrderMap.set(child.id, currentOrder);
        currentOrder++;
        // Recursively process this layer's children
        currentOrder = buildRenderOrder(child.id, currentOrder);
      }
      return currentOrder;
    };

    // Start with default layer at order 0
    layerRenderOrderMap.set('default', 0);
    // Build order for all root layers and their descendants, starting after default
    buildRenderOrder(null, 1);

    // Create unified render items with type discrimination
    type RenderItem =
      | { type: 'prop'; item: PropInstance; layerId: string; zIndex: number }
      | { type: 'path'; item: PenPathInstance; layerId: string; zIndex: number }
      | { type: 'text'; item: TextInstance; layerId: string; zIndex: number }
      | { type: 'shape'; item: ShapeInstance; layerId: string; zIndex: number };

    const renderItems: RenderItem[] = [];

    // Add props
    if (props.length > 0 && propImages) {
      for (const prop of props) {
        renderItems.push({
          type: 'prop',
          item: prop,
          layerId: prop.layerId || 'default',
          zIndex: prop.zIndex || 0
        });
      }
    }

    // Add pen paths
    for (const path of penPaths) {
      renderItems.push({
        type: 'path',
        item: path,
        layerId: path.layerId || 'default',
        zIndex: path.zIndex || 0
      });
    }

    // Add texts
    for (const text of texts) {
      renderItems.push({
        type: 'text',
        item: text,
        layerId: text.layerId || 'default',
        zIndex: text.zIndex || 0
      });
    }

    // Add shapes
    for (const shape of shapes) {
      renderItems.push({
        type: 'shape',
        item: shape,
        layerId: shape.layerId || 'default',
        zIndex: shape.zIndex || 0
      });
    }

    // Sort by hierarchical layer order (ascending), then by zIndex within layer (ascending)
    renderItems.sort((a, b) => {
      const layerOrderA = layerRenderOrderMap.get(a.layerId) ?? 0;
      const layerOrderB = layerRenderOrderMap.get(b.layerId) ?? 0;
      if (layerOrderA !== layerOrderB) {
        return layerOrderA - layerOrderB;
      }
      return a.zIndex - b.zIndex;
    });

    // Render all items in unified order
    for (const renderItem of renderItems) {
      if (renderItem.type === 'prop') {
        const prop = renderItem.item;
        const img = propImages?.get(prop.propName);
        if (!img) continue;

        const screenX = (prop.x - viewport.x) * viewport.zoom + centerX;
        const screenY = (prop.y - viewport.y) * viewport.zoom + centerY;

        // Get the prop's actual scale values (handle scaleX/scaleY if available)
        const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
        const scaleX = propAny.scaleX ?? prop.scale;
        const scaleY = propAny.scaleY ?? prop.scale;

        // Use absolute values for dimensions, apply sign via ctx.scale for flip
        const absScaleX = Math.abs(scaleX);
        const absScaleY = Math.abs(scaleY);
        const flipX = scaleX < 0 ? -1 : 1;
        const flipY = scaleY < 0 ? -1 : 1;
        const scaledWidth = img.width * absScaleX * viewport.zoom;
        const scaledHeight = img.height * absScaleY * viewport.zoom;

        // Get layer info for opacity
        const propLayer = layers.find(l => l.id === renderItem.layerId);
        let layerOpacity = (propLayer?.opacity ?? 100) / 100;
        // Apply hover feedback: reduce opacity if this layer is being hovered
        if (hoveredViewLayerIdRef.current === renderItem.layerId) {
          layerOpacity *= 0.5;
        }

        ctx.save();
        ctx.translate(screenX, screenY);
        ctx.rotate((prop.rotation * Math.PI) / 180);
        ctx.scale(flipX, flipY);

        // Apply layer opacity
        ctx.globalAlpha = layerOpacity;

        // Draw selection outline if selected
        const isSelected = selectedPropIds.includes(prop.id);
        if (isSelected) {
          ctx.strokeStyle = '#00aaff';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(-scaledWidth / 2 - 2, -scaledHeight / 2 - 2, scaledWidth + 4, scaledHeight + 4);
          ctx.setLineDash([]);
        }

        ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();

        // Draw handles only for single selection (when exactly one prop is selected)
        if (isSelected && selectedPropIds.length === 1 && activeTool === 'prop') {
          const handles = getHandlePositions(prop);
          if (handles) {
            const handleSize = 8;

            // Draw corner handles (resize)
            ctx.fillStyle = '#ffffff';
            ctx.strokeStyle = '#00aaff';
            ctx.lineWidth = 2;

            for (const corner of ['tl', 'tr', 'bl', 'br'] as const) {
              const pos = handles[corner];
              ctx.beginPath();
              ctx.rect(pos.x - handleSize / 2, pos.y - handleSize / 2, handleSize, handleSize);
              ctx.fill();
              ctx.stroke();
            }

            // Draw rotation handle
            const rotatePos = handles.rotate;
            // Draw line from top center to rotation handle
            const topCenter = {
              x: (handles.tl.x + handles.tr.x) / 2,
              y: (handles.tl.y + handles.tr.y) / 2
            };
            ctx.strokeStyle = '#00aaff';
            ctx.lineWidth = 1;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo(topCenter.x, topCenter.y);
            ctx.lineTo(rotatePos.x, rotatePos.y);
            ctx.stroke();
            ctx.setLineDash([]);

            // Draw rotation handle circle
            ctx.fillStyle = '#00aaff';
            ctx.beginPath();
            ctx.arc(rotatePos.x, rotatePos.y, handleSize / 2 + 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(rotatePos.x, rotatePos.y, handleSize / 2 - 1, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else if (renderItem.type === 'path') {
        const path = renderItem.item;
        const isSelected = selectedPenPathIds.includes(path.id);
        const isEditing = isSelected && activeTool === 'pen';

        // Apply hover feedback for paths
        const pathLayer = layers.find(l => l.id === renderItem.layerId);
        let pathOpacity = (pathLayer?.opacity ?? 100) / 100;
        if (hoveredViewLayerIdRef.current === renderItem.layerId) {
          pathOpacity *= 0.5;
        }

        ctx.save();
        ctx.globalAlpha = pathOpacity;
        // Get selected anchor indices for this path
        const pathSelectedAnchors = selectedAnchorIndices.get(path.id) ?? new Set<number>();
        drawPenPath(
          ctx,
          path,
          viewport.zoom,
          viewport.x,
          viewport.y,
          centerX,
          centerY,
          isSelected,
          isEditing,
          pathSelectedAnchors
        );
        ctx.restore();
      } else if (renderItem.type === 'text') {
        const text = renderItem.item;
        // Skip text being edited inline (it will be rendered as HTML)
        if (editingTextId === text.id) continue;

        const screenX = (text.x - viewport.x) * viewport.zoom + centerX;
        const screenY = (text.y - viewport.y) * viewport.zoom + centerY;
        // For fixed size mode, textbox dimensions stay constant regardless of zoom
        const scaledWidth = text.fixedSize ? text.width : text.width * viewport.zoom;
        const scaledHeight = text.fixedSize ? text.height : text.height * viewport.zoom;
        // Auto-calculate font size based on textbox height (70% of height)
        const autoFontSize = text.height * 0.7;
        // For fixed size mode, font size stays constant regardless of zoom
        const scaledFontSize = text.fixedSize ? autoFontSize : autoFontSize * viewport.zoom;

        // Get layer opacity
        const textLayer = layers.find(l => l.id === renderItem.layerId);
        let layerOpacity = (textLayer?.opacity ?? 100) / 100;
        // Apply hover feedback: reduce opacity if this layer is being hovered
        if (hoveredViewLayerIdRef.current === renderItem.layerId) {
          layerOpacity *= 0.5;
        }

        ctx.save();
        ctx.translate(screenX, screenY);
        ctx.rotate((text.rotation * Math.PI) / 180);
        ctx.globalAlpha = layerOpacity;

        // Draw selection outline if selected
        const isSelected = selectedTextIds.includes(text.id);
        if (isSelected) {
          ctx.strokeStyle = '#00aaff';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(-scaledWidth / 2 - 2, -scaledHeight / 2 - 2, scaledWidth + 4, scaledHeight + 4);
          ctx.setLineDash([]);
        }

        // Configure text rendering
        ctx.font = `${text.fontWeight} ${scaledFontSize}px ${text.fontFamily}`;
        ctx.textAlign = text.alignment;
        ctx.textBaseline = 'top';

        // Calculate text position based on alignment
        const padding = text.fixedSize ? 4 : 4 * viewport.zoom;
        let textX = -scaledWidth / 2 + padding;
        if (text.alignment === 'center') {
          textX = 0;
        } else if (text.alignment === 'right') {
          textX = scaledWidth / 2 - padding;
        }

        // Word wrap the text content
        const maxWidth = scaledWidth - padding * 2;
        const lineHeight = scaledFontSize * 1.2;
        const words = text.content.split(/(\s+)/); // Keep whitespace as separate tokens
        const lines: string[] = [];
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine + word;
          const testWidth = ctx.measureText(testLine).width;

          if (testWidth > maxWidth && currentLine !== '') {
            lines.push(currentLine.trimEnd());
            currentLine = word.trimStart();
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          lines.push(currentLine.trimEnd());
        }

        // If no lines, add empty line for cursor
        if (lines.length === 0) {
          lines.push('');
        }

        // Calculate starting Y position for vertical centering
        const totalTextHeight = lines.length * lineHeight;
        let startY = -scaledHeight / 2 + padding;

        // Vertically center if text fits
        if (totalTextHeight < scaledHeight - padding * 2) {
          startY = -totalTextHeight / 2;
        }

        // Draw each line
        for (let i = 0; i < lines.length; i++) {
          const lineY = startY + i * lineHeight;

          // Draw stroke if enabled
          if (text.strokeColor && text.strokeWidth > 0) {
            ctx.strokeStyle = text.strokeColor;
            // For fixed size mode, stroke width stays constant too
            ctx.lineWidth = text.fixedSize ? text.strokeWidth : text.strokeWidth * viewport.zoom;
            ctx.lineJoin = 'round';
            ctx.strokeText(lines[i], textX, lineY);
          }

          // Draw text fill
          ctx.fillStyle = text.color;
          ctx.fillText(lines[i], textX, lineY);
        }

        ctx.restore();

        // Draw handles for selected text (single selection only)
        if (isSelected && selectedTextIds.length === 1 && activeTool === 'text') {
          const handleSize = 8;

          // Calculate handle positions
          const rad = (text.rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);
          const halfW = scaledWidth / 2;
          const halfH = scaledHeight / 2;

          const corners = {
            tl: { x: -halfW, y: -halfH },
            tr: { x: halfW, y: -halfH },
            bl: { x: -halfW, y: halfH },
            br: { x: halfW, y: halfH }
          };

          const rotateCorner = (c: { x: number; y: number }) => ({
            x: screenX + c.x * cos - c.y * sin,
            y: screenY + c.x * sin + c.y * cos
          });

          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#00aaff';
          ctx.lineWidth = 2;

          for (const key of ['tl', 'tr', 'bl', 'br'] as const) {
            const pos = rotateCorner(corners[key]);
            ctx.beginPath();
            ctx.rect(pos.x - handleSize / 2, pos.y - handleSize / 2, handleSize, handleSize);
            ctx.fill();
            ctx.stroke();
          }

          // Rotation handle
          const rotateHandleOffset = { x: 0, y: -halfH - 30 };
          const rotatePos = {
            x: screenX + rotateHandleOffset.x * cos - rotateHandleOffset.y * sin,
            y: screenY + rotateHandleOffset.x * sin + rotateHandleOffset.y * cos
          };
          const topCenter = rotateCorner({ x: 0, y: -halfH });

          ctx.strokeStyle = '#00aaff';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(topCenter.x, topCenter.y);
          ctx.lineTo(rotatePos.x, rotatePos.y);
          ctx.stroke();
          ctx.setLineDash([]);

          ctx.fillStyle = '#00aaff';
          ctx.beginPath();
          ctx.arc(rotatePos.x, rotatePos.y, handleSize / 2 + 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(rotatePos.x, rotatePos.y, handleSize / 2 - 1, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (renderItem.type === 'shape') {
        const shape = renderItem.item;
        const isSelected = selectedShapeIds.includes(shape.id);
        const isEditing = isSelected && activeTool === 'shape';

        // Get layer opacity
        const shapeLayer = layers.find(l => l.id === renderItem.layerId);
        let layerOpacity = (shapeLayer?.opacity ?? 100) / 100;
        // Apply hover feedback: reduce opacity if this layer is being hovered
        if (hoveredViewLayerIdRef.current === renderItem.layerId) {
          layerOpacity *= 0.5;
        }

        drawShape(
          ctx,
          shape,
          viewport.zoom,
          viewport.x,
          viewport.y,
          centerX,
          centerY,
          isSelected,
          isEditing,
          layerOpacity
        );
      }
    }

    // Draw multi-selection bounding box and handles (when multiple props are selected)
    if (selectedPropIds.length > 1 && activeTool === 'prop') {
      const multiBounds = getMultiSelectionBounds();
      if (multiBounds) {
        const { handles } = multiBounds;
        const handleSize = 8;

        // Draw bounding box
        const boxX = handles.tl.x;
        const boxY = handles.tl.y;
        const boxW = handles.tr.x - handles.tl.x;
        const boxH = handles.bl.y - handles.tl.y;

        ctx.strokeStyle = '#00aaff';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(boxX, boxY, boxW, boxH);
        ctx.setLineDash([]);

        // Draw corner handles (resize)
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#00aaff';
        ctx.lineWidth = 2;

        for (const corner of ['tl', 'tr', 'bl', 'br'] as const) {
          const pos = handles[corner];
          ctx.beginPath();
          ctx.rect(pos.x - handleSize / 2, pos.y - handleSize / 2, handleSize, handleSize);
          ctx.fill();
          ctx.stroke();
        }

        // Draw rotation handle
        const rotatePos = handles.rotate;
        const topCenter = {
          x: (handles.tl.x + handles.tr.x) / 2,
          y: handles.tl.y
        };
        ctx.strokeStyle = '#00aaff';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(topCenter.x, topCenter.y);
        ctx.lineTo(rotatePos.x, rotatePos.y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw rotation handle circle
        ctx.fillStyle = '#00aaff';
        ctx.beginPath();
        ctx.arc(rotatePos.x, rotatePos.y, handleSize / 2 + 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(rotatePos.x, rotatePos.y, handleSize / 2 - 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw path being created (always on top)
    if (isDrawingPath && currentPathPoints.length > 0 && penSettings) {
      drawPathPreview(
        ctx,
        currentPathPoints,
        mouseWorldPos,
        tempHandleRef.current,
        {
          strokeColor: penSettings.strokeColor,
          strokeWidth: penSettings.strokeWidth,
          strokeOpacity: penSettings.strokeOpacity,
          lineCap: penSettings.lineCap,
          lineJoin: penSettings.lineJoin,
          dashPreset: penSettings.dashPreset,
          fillColor: penSettings.fillColor,
          fillOpacity: penSettings.fillOpacity
        },
        viewport.zoom,
        viewport.x,
        viewport.y,
        centerX,
        centerY
      );
    }

    // Draw shape being created (always on top)
    if (shapeDrawStartRef.current && shapeDrawCurrentRef.current && shapeSettings) {
      drawShapePreview(
        ctx,
        shapeSettings.selectedShapeType,
        shapeDrawStartRef.current,
        shapeDrawCurrentRef.current,
        shapeSettings,
        viewport.zoom,
        viewport.x,
        viewport.y,
        centerX,
        centerY,
        isShiftHeldRef.current,
        isAltHeldRef.current
      );
    }

    // Draw text placement preview
    if (activeTool === 'text' && lastMousePos && selectedTextIds.length === 0 && !isDraggingTextRef.current) {
      const worldPos = screenToWorld(lastMousePos.x, lastMousePos.y);
      const screenX = (worldPos.x - viewport.x) * viewport.zoom + centerX;
      const screenY = (worldPos.y - viewport.y) * viewport.zoom + centerY;
      const previewWidth = 200 * viewport.zoom;
      const previewHeight = 50 * viewport.zoom;

      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = '#00aaff';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(screenX - previewWidth / 2, screenY - previewHeight / 2, previewWidth, previewHeight);
      ctx.setLineDash([]);

      ctx.fillStyle = '#00aaff';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Click to add text', screenX, screenY);
      ctx.restore();
    }

    // Draw prop placement preview (only when no props are selected)
    if (activeTool === 'prop' && propSettings?.selectedProp && propImages && lastMousePos && !isDraggingPropRef.current && selectedPropIds.length === 0 && !isSelectionBoxActiveRef.current) {
      const propImg = propImages.get(propSettings.selectedProp);
      if (propImg) {
        const worldPos = screenToWorld(lastMousePos.x, lastMousePos.y);
        const screenX = (worldPos.x - viewport.x) * viewport.zoom + centerX;
        const screenY = (worldPos.y - viewport.y) * viewport.zoom + centerY;
        const scaledWidth = propImg.width * propSettings.scale * viewport.zoom;
        const scaledHeight = propImg.height * propSettings.scale * viewport.zoom;

        // Check if placement is allowed at current zoom
        const canPlace = canPlacePropAtZoom();

        ctx.save();
        ctx.globalAlpha = canPlace ? 0.6 : 0.3;
        ctx.translate(screenX, screenY);
        ctx.rotate((propSettings.rotation * Math.PI) / 180);

        // If placement not allowed, tint red
        if (!canPlace) {
          // Draw with red tint overlay
          ctx.drawImage(propImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          ctx.globalCompositeOperation = 'source-atop';
          ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
          ctx.fillRect(-scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        } else {
          ctx.drawImage(propImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        }

        ctx.restore();
      }
    }

    // Draw cursor preview for brush or eraser tools
    const showCursorPreview = (activeTool === 'brush' || activeTool === 'eraser') &&
                               brushSettings.brushName &&
                               lastMousePos &&
                               !isPanning;

    if (showCursorPreview) {
      const worldPos = screenToWorld(lastMousePos.x, lastMousePos.y);

      const screenX = (worldPos.x - viewport.x) * viewport.zoom + centerX;
      const screenY = (worldPos.y - viewport.y) * viewport.zoom + centerY;
      const screenSize = Math.round(brushSettings.size * viewport.zoom);

      const brushImg = brushSettings.brushName ? brushImages.get(brushSettings.brushName) : null;
      const materialImg = activeTool === 'brush' && brushSettings.materialName
        ? materialImages.get(brushSettings.materialName)
        : null;

      if (brushImg && screenSize > 0) {
        // Create preview canvas
        const previewCanvas = document.createElement('canvas');
        previewCanvas.width = screenSize;
        previewCanvas.height = screenSize;
        const previewCtx = previewCanvas.getContext('2d');

        if (previewCtx) {
          // Create mask from brush
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = screenSize;
          maskCanvas.height = screenSize;
          const maskCtx = maskCanvas.getContext('2d');

          if (maskCtx) {
            maskCtx.drawImage(brushImg, 0, 0, screenSize, screenSize);

            const imageData = maskCtx.getImageData(0, 0, screenSize, screenSize);
            const data = imageData.data;
            const previewStrength = brushSettings.strength * 0.5;

            if (activeTool === 'eraser') {
              // Eraser preview: red tint
              for (let i = 0; i < data.length; i += 4) {
                const luminosity = data[i];
                data[i] = 255;
                data[i + 1] = 100;
                data[i + 2] = 100;
                data[i + 3] = Math.round(luminosity * previewStrength);
              }
            } else {
              // Brush preview: white mask
              for (let i = 0; i < data.length; i += 4) {
                const luminosity = data[i];
                data[i] = 255;
                data[i + 1] = 255;
                data[i + 2] = 255;
                data[i + 3] = Math.round(luminosity * previewStrength);
              }
            }

            maskCtx.putImageData(imageData, 0, 0);

            if (activeTool === 'brush' && materialImg) {
              // Draw tiled material for brush
              const tileSize = Math.min(materialImg.width, materialImg.height, screenSize);
              const tilesX = Math.ceil(screenSize / tileSize);
              const tilesY = Math.ceil(screenSize / tileSize);

              for (let tx = 0; tx < tilesX; tx++) {
                for (let ty = 0; ty < tilesY; ty++) {
                  previewCtx.drawImage(
                    materialImg,
                    0, 0, materialImg.width, materialImg.height,
                    tx * tileSize, ty * tileSize, tileSize, tileSize
                  );
                }
              }

              // Apply mask
              previewCtx.globalCompositeOperation = 'destination-in';
              previewCtx.drawImage(maskCanvas, 0, 0);
            } else {
              // For eraser, just use the mask directly
              previewCtx.drawImage(maskCanvas, 0, 0);
            }

            // Draw preview to main canvas with rotation
            // Use previewRotation for stroke/continuous modes, otherwise use fixed rotation
            const displayRotation = brushSettings.rotationMode !== 'fixed'
              ? previewRotation
              : brushSettings.rotation;

            ctx.save();
            ctx.translate(screenX, screenY);
            ctx.rotate((displayRotation * Math.PI) / 180);
            ctx.drawImage(previewCanvas, -screenSize / 2, -screenSize / 2);
            ctx.restore();
          }
        }
      }

      // Draw outline
      ctx.strokeStyle = activeTool === 'eraser' ? 'rgba(255, 100, 100, 0.5)' : 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.rect(
        Math.round(screenX - screenSize / 2) + 0.5,
        Math.round(screenY - screenSize / 2) + 0.5,
        screenSize,
        screenSize
      );
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw grid (only if enabled) - rendered last to be on top of everything
    // Grid adapts to zoom level: 1m -> 10m -> 100m -> 1km -> 10km -> 100km -> 1000km
    if (showGrid) {
      // Determine grid step in meters based on zoom level
      // Target: grid cells should be between 20-200 pixels on screen
      const MIN_GRID_PIXELS = 20;

      // Available grid steps in meters (powers of 10, up to 1 million km for planetary maps)
      const gridSteps = [1, 10, 100, 1000, 10000, 100000, 1000000, 10000000, 100000000, 1000000000];

      // Find the appropriate grid step
      let gridMeters = 1;
      for (const step of gridSteps) {
        const screenSize = step * scale * viewport.zoom;
        if (screenSize >= MIN_GRID_PIXELS) {
          gridMeters = step;
          break;
        }
        gridMeters = step;
      }

      const gridSize = gridMeters * scale * viewport.zoom;

      if (gridSize > 4) {
        // Draw minor grid lines (lighter)
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.lineWidth = 1;

        const offsetX = (((-viewport.x * viewport.zoom) % gridSize) + gridSize) % gridSize + centerX % gridSize;
        const offsetY = (((-viewport.y * viewport.zoom) % gridSize) + gridSize) % gridSize + centerY % gridSize;

        ctx.beginPath();
        for (let x = offsetX - gridSize; x < width + gridSize; x += gridSize) {
          ctx.moveTo(Math.round(x) + 0.5, 0);
          ctx.lineTo(Math.round(x) + 0.5, height);
        }
        for (let y = offsetY - gridSize; y < height + gridSize; y += gridSize) {
          ctx.moveTo(0, Math.round(y) + 0.5);
          ctx.lineTo(width, Math.round(y) + 0.5);
        }
        ctx.stroke();

        // Draw major grid lines (every 10 units, darker)
        const majorGridSize = gridSize * 10;
        if (majorGridSize < width * 2) {
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
          ctx.lineWidth = 1;

          const majorOffsetX = (((-viewport.x * viewport.zoom) % majorGridSize) + majorGridSize) % majorGridSize + centerX % majorGridSize;
          const majorOffsetY = (((-viewport.y * viewport.zoom) % majorGridSize) + majorGridSize) % majorGridSize + centerY % majorGridSize;

          ctx.beginPath();
          for (let x = majorOffsetX - majorGridSize; x < width + majorGridSize; x += majorGridSize) {
            ctx.moveTo(Math.round(x) + 0.5, 0);
            ctx.lineTo(Math.round(x) + 0.5, height);
          }
          for (let y = majorOffsetY - majorGridSize; y < height + majorGridSize; y += majorGridSize) {
            ctx.moveTo(0, Math.round(y) + 0.5);
            ctx.lineTo(width, Math.round(y) + 0.5);
          }
          ctx.stroke();
        }

      }
    }

    // Draw measurement tools
    const drawDistanceLabel = (text: string, x: number, y: number, color: string, small: boolean = false): void => {
      ctx.font = small ? '11px monospace' : '13px monospace';
      const metrics = ctx.measureText(text);
      const padding = small ? 3 : 5;
      const labelHeight = small ? 16 : 20;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillRect(x - metrics.width / 2 - padding, y - labelHeight / 2, metrics.width + padding * 2, labelHeight);

      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.strokeRect(x - metrics.width / 2 - padding, y - labelHeight / 2, metrics.width + padding * 2, labelHeight);

      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x, y);
    };

    // Draw ruler (straight line)
    if (activeMeasureTool === 'ruler' && rulerStartRef.current) {
      const startScreen = worldToScreen(rulerStartRef.current.x, rulerStartRef.current.y);
      const endPoint = rulerEndRef.current || rulerStartRef.current;
      const endScreen = worldToScreen(endPoint.x, endPoint.y);

      ctx.strokeStyle = '#00ff88';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(startScreen.x, startScreen.y);
      ctx.lineTo(endScreen.x, endScreen.y);
      ctx.stroke();

      ctx.fillStyle = '#00ff88';
      ctx.beginPath();
      ctx.arc(startScreen.x, startScreen.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(endScreen.x, endScreen.y, 5, 0, Math.PI * 2);
      ctx.fill();

      if (rulerEndRef.current) {
        const distance = calculateDistance(rulerStartRef.current, rulerEndRef.current);
        const midX = (startScreen.x + endScreen.x) / 2;
        const midY = (startScreen.y + endScreen.y) / 2;
        drawDistanceLabel(formatDistance(distance), midX, midY - 15, '#00ff88');
      }
    }

    // Draw tape measure (polyline)
    if (activeMeasureTool === 'tape') {
      const points = tapePointsRef.current;
      const tempPoint = tapeTempPointRef.current;

      if (points.length > 0 || tempPoint) {
        ctx.strokeStyle = '#ffaa00';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);

        if (points.length > 0) {
          ctx.beginPath();
          const firstScreen = worldToScreen(points[0].x, points[0].y);
          ctx.moveTo(firstScreen.x, firstScreen.y);

          for (let i = 1; i < points.length; i++) {
            const screen = worldToScreen(points[i].x, points[i].y);
            ctx.lineTo(screen.x, screen.y);
          }

          if (tempPoint) {
            const tempScreen = worldToScreen(tempPoint.x, tempPoint.y);
            ctx.lineTo(tempScreen.x, tempScreen.y);
          }

          ctx.stroke();
        }

        ctx.fillStyle = '#ffaa00';
        for (const point of points) {
          const screen = worldToScreen(point.x, point.y);
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, 5, 0, Math.PI * 2);
          ctx.fill();
        }

        for (let i = 1; i < points.length; i++) {
          const p1 = points[i - 1];
          const p2 = points[i];
          const s1 = worldToScreen(p1.x, p1.y);
          const s2 = worldToScreen(p2.x, p2.y);
          const segmentDist = calculateDistance(p1, p2);
          const midX = (s1.x + s2.x) / 2;
          const midY = (s1.y + s2.y) / 2;
          drawDistanceLabel(formatDistance(segmentDist), midX, midY - 10, '#ffaa00', true);
        }

        if (tempPoint && points.length > 0) {
          const lastPoint = points[points.length - 1];
          const lastScreen = worldToScreen(lastPoint.x, lastPoint.y);
          const tempScreen = worldToScreen(tempPoint.x, tempPoint.y);
          const segmentDist = calculateDistance(lastPoint, tempPoint);
          const midX = (lastScreen.x + tempScreen.x) / 2;
          const midY = (lastScreen.y + tempScreen.y) / 2;
          drawDistanceLabel(formatDistance(segmentDist), midX, midY - 10, '#ffaa00', true);
        }

        if (tempPoint) {
          const allPoints = [...points, tempPoint];
          const totalDist = calculatePolylineDistance(allPoints);
          const tempScreen = worldToScreen(tempPoint.x, tempPoint.y);
          drawDistanceLabel(`Total: ${formatDistance(totalDist)}`, tempScreen.x, tempScreen.y - 25, '#ffaa00');
        } else if (points.length > 1) {
          const totalDist = calculatePolylineDistance(points);
          const lastScreen = worldToScreen(points[points.length - 1].x, points[points.length - 1].y);
          drawDistanceLabel(`Total: ${formatDistance(totalDist)}`, lastScreen.x, lastScreen.y - 25, '#ffaa00');
        }
      }
    }

    // Draw selection box
    if (isSelectionBoxActiveRef.current && selectionBoxStartRef.current && selectionBoxEndRef.current) {
      const startScreen = worldToScreen(selectionBoxStartRef.current.x, selectionBoxStartRef.current.y);
      const endScreen = worldToScreen(selectionBoxEndRef.current.x, selectionBoxEndRef.current.y);

      const boxX = Math.min(startScreen.x, endScreen.x);
      const boxY = Math.min(startScreen.y, endScreen.y);
      const boxW = Math.abs(endScreen.x - startScreen.x);
      const boxH = Math.abs(endScreen.y - startScreen.y);

      // Fill
      ctx.fillStyle = 'rgba(0, 170, 255, 0.1)';
      ctx.fillRect(boxX, boxY, boxW, boxH);

      // Border
      ctx.strokeStyle = '#00aaff';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(boxX + 0.5, boxY + 0.5, boxW, boxH);
      ctx.setLineDash([]);
    }
  }, [viewport, activeTool, brushSettings, lastMousePos, screenToWorld, worldToScreen, materialImages, brushImages, tileManager, isPanning, backgroundMaterial, backgroundRandomRotation, showGrid, mapId, scale, activeMeasureTool, calculateDistance, calculatePolylineDistance, props, propImages, propSettings, selectedPropIds, getHandlePositions, previewRotation, canPlacePropAtZoom, layers, activeLayerId, penPaths, selectedPenPathIds, penSettings, isDrawingPath, currentPathPoints, mouseWorldPos]);

  // Ref for throttling tile and prop loading
  const lastTileLoadRef = useRef<number>(0);
  const lastPropLoadRef = useRef<number>(0);

  /**
   * Load visible regions when viewport changes
   * Throttled to avoid excessive loading during rapid zoom/pan
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const now = Date.now();

    // Load tiles for visible area (throttled)
    if (tileManager) {
      // Throttle more aggressively at low zoom levels
      const throttleMs = viewport.zoom < 0.1 ? 200 : 50;

      if (now - lastTileLoadRef.current > throttleMs) {
        lastTileLoadRef.current = now;
        tileManager.ensureTilesLoaded(
          viewport.x,
          viewport.y,
          rect.width,
          rect.height,
          viewport.zoom
        );
      }
    }

    // Notify parent of viewport change (for prop loading, etc.)
    // Throttle more aggressively at low zoom to avoid prop region flood
    const propThrottleMs = viewport.zoom < 0.1 ? 500 : 100;
    if (now - lastPropLoadRef.current > propThrottleMs) {
      lastPropLoadRef.current = now;
      onViewportChange?.(viewport.x, viewport.y, rect.width, rect.height, viewport.zoom);
    }
  }, [viewport, tileManager, onViewportChange]);

  /**
   * Animation loop
   */
  useEffect(() => {
    let rafId: number;
    let running = true;

    const animate = (): void => {
      if (!running) return;
      render();
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
    };
  }, [render]);

  /**
   * Notify parent of zoom changes
   */
  useEffect(() => {
    onZoomChange?.(viewport.zoom);
  }, [viewport.zoom, onZoomChange]);

  /**
   * Notify parent of mouse world position changes
   */
  useEffect(() => {
    onMouseWorldPosChange?.(mouseWorldPos);
  }, [mouseWorldPos, onMouseWorldPosChange]);

  /**
   * Prevent browser zoom shortcuts (Ctrl+wheel, Ctrl++, Ctrl+-, Ctrl+0)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '0') {
          e.preventDefault();
        }
      }
    };

    const handleWheelEvent = (e: Event): void => {
      if ((e as globalThis.WheelEvent).ctrlKey || (e as globalThis.WheelEvent).metaKey) {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheelEvent, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheelEvent);
    };
  }, []);

  /**
   * Handles mouse wheel for zooming
   * Attached via useEffect with { passive: false } to allow preventDefault
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: globalThis.WheelEvent): void => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      const worldX = (mouseX - centerX) / viewportRef.current.zoom + viewportRef.current.x;
      const worldY = (mouseY - centerY) / viewportRef.current.zoom + viewportRef.current.y;

      const zoomDelta = -e.deltaY * ZOOM_SENSITIVITY;
      const newZoom = Math.min(MAX_VIEWPORT_ZOOM, Math.max(MIN_VIEWPORT_ZOOM, viewportRef.current.zoom * (1 + zoomDelta)));

      const newX = worldX - (mouseX - centerX) / newZoom;
      const newY = worldY - (mouseY - centerY) / newZoom;

      setViewport({ x: newX, y: newY, zoom: newZoom });
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, []);

  /**
   * Handles touch events for mobile pan and pinch-to-zoom
   * Supports single finger pan and two finger pinch zoom
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /**
     * Calculates the distance between two touch points
     */
    const getTouchDistance = (touches: TouchList): number => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    /**
     * Calculates the center point between two touches
     */
    const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };

    /**
     * Converts screen touch coordinates to world coordinates
     */
    const touchToWorld = (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = canvas.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      return {
        x: (clientX - rect.left - centerX) / viewportRef.current.zoom + viewportRef.current.x,
        y: (clientY - rect.top - centerY) / viewportRef.current.zoom + viewportRef.current.y
      };
    };

    /**
     * Handles touch start - initializes pan or pinch zoom
     */
    const handleTouchStart = (e: TouchEvent): void => {
      e.preventDefault();

      // Update mouse world position for coordinates display (use first touch)
      if (e.touches.length >= 1) {
        const touchWorldPos = touchToWorld(e.touches[0].clientX, e.touches[0].clientY);
        setMouseWorldPos(touchWorldPos);
      }

      if (e.touches.length === 1) {
        // Single finger: start panning
        isTouchPanningRef.current = true;
        isPinchZoomingRef.current = false;
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
        lastTouchDistanceRef.current = null;
        lastTouchCenterRef.current = null;
      } else if (e.touches.length === 2) {
        // Two fingers: start pinch zoom
        isTouchPanningRef.current = false;
        isPinchZoomingRef.current = true;
        lastTouchDistanceRef.current = getTouchDistance(e.touches);
        lastTouchCenterRef.current = getTouchCenter(e.touches);
        touchStartRef.current = null;
      }
    };

    /**
     * Handles touch move - performs pan or pinch zoom
     */
    const handleTouchMove = (e: TouchEvent): void => {
      e.preventDefault();

      // Update mouse world position for coordinates display (use first touch)
      if (e.touches.length >= 1) {
        const touchWorldPos = touchToWorld(e.touches[0].clientX, e.touches[0].clientY);
        setMouseWorldPos(touchWorldPos);
      }

      if (e.touches.length === 1 && isTouchPanningRef.current && touchStartRef.current) {
        // Single finger pan
        const dx = (e.touches[0].clientX - touchStartRef.current.x) / viewportRef.current.zoom;
        const dy = (e.touches[0].clientY - touchStartRef.current.y) / viewportRef.current.zoom;

        setViewport((prev) => ({
          ...prev,
          x: prev.x - dx,
          y: prev.y - dy
        }));

        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
      } else if (e.touches.length === 2 && isPinchZoomingRef.current) {
        // Two finger pinch zoom
        const currentDistance = getTouchDistance(e.touches);
        const currentCenter = getTouchCenter(e.touches);

        if (lastTouchDistanceRef.current !== null && lastTouchCenterRef.current !== null) {
          const rect = canvas.getBoundingClientRect();
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;

          // Calculate zoom center in screen coordinates
          const touchCenterX = currentCenter.x - rect.left;
          const touchCenterY = currentCenter.y - rect.top;

          // Convert touch center to world coordinates
          const worldX = (touchCenterX - centerX) / viewportRef.current.zoom + viewportRef.current.x;
          const worldY = (touchCenterY - centerY) / viewportRef.current.zoom + viewportRef.current.y;

          // Calculate zoom factor from pinch
          const zoomFactor = currentDistance / lastTouchDistanceRef.current;
          const newZoom = Math.min(MAX_VIEWPORT_ZOOM, Math.max(MIN_VIEWPORT_ZOOM, viewportRef.current.zoom * zoomFactor));

          // Also handle pan during pinch (two finger drag)
          const panDx = (currentCenter.x - lastTouchCenterRef.current.x) / viewportRef.current.zoom;
          const panDy = (currentCenter.y - lastTouchCenterRef.current.y) / viewportRef.current.zoom;

          // Calculate new viewport position to keep touch center fixed
          const newX = worldX - (touchCenterX - centerX) / newZoom - panDx;
          const newY = worldY - (touchCenterY - centerY) / newZoom - panDy;

          setViewport({ x: newX, y: newY, zoom: newZoom });
        }

        lastTouchDistanceRef.current = currentDistance;
        lastTouchCenterRef.current = currentCenter;
      } else if (e.touches.length === 2 && !isPinchZoomingRef.current) {
        // Switched from 1 finger to 2 fingers - initialize pinch zoom
        isTouchPanningRef.current = false;
        isPinchZoomingRef.current = true;
        lastTouchDistanceRef.current = getTouchDistance(e.touches);
        lastTouchCenterRef.current = getTouchCenter(e.touches);
        touchStartRef.current = null;
      }
    };

    /**
     * Handles touch end - resets touch state
     */
    const handleTouchEnd = (e: TouchEvent): void => {
      e.preventDefault();

      if (e.touches.length === 0) {
        // All fingers lifted
        isTouchPanningRef.current = false;
        isPinchZoomingRef.current = false;
        touchStartRef.current = null;
        lastTouchDistanceRef.current = null;
        lastTouchCenterRef.current = null;
      } else if (e.touches.length === 1) {
        // One finger remaining - switch to pan mode
        isPinchZoomingRef.current = false;
        isTouchPanningRef.current = true;
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        };
        lastTouchDistanceRef.current = null;
        lastTouchCenterRef.current = null;
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  /**
   * Handles mouse down for panning or drawing
   */
  const handleMouseDown = useCallback((e: MouseEvent<HTMLCanvasElement>): void => {
    const worldPos = screenToWorld(e.clientX, e.clientY);
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    const screenX = rect ? e.clientX - rect.left : 0;
    const screenY = rect ? e.clientY - rect.top : 0;

    // Track modifier keys
    isCtrlHeldRef.current = e.ctrlKey;
    isShiftHeldRef.current = e.shiftKey;

    // Store mouse down position for determining if we're starting a selection box
    mouseDownPosRef.current = worldPos;

    // Shift+Click from ANY tool: toggle prop selection and switch to prop tool
    if (e.button === 0 && e.shiftKey && !activeMeasureTool) {
      const clickedProp = findPropAtPosition(worldPos.x, worldPos.y);
      if (clickedProp) {
        // Toggle selection: add or remove from selection
        const currentSelection = [...selectedPropIds];
        const index = currentSelection.indexOf(clickedProp.id);
        if (index >= 0) {
          // Already selected - remove from selection
          currentSelection.splice(index, 1);
          onPropSelect?.(currentSelection, true);
        } else {
          // Not selected - add to selection
          currentSelection.push(clickedProp.id);
          onPropSelect?.(currentSelection, true);
        }
        return;
      }
    }

    // Handle measurement tools
    if (e.button === 0 && activeMeasureTool) {
      if (activeMeasureTool === 'ruler') {
        rulerStartRef.current = worldPos;
        rulerEndRef.current = worldPos;
        isRulerActiveRef.current = true;
      } else if (activeMeasureTool === 'tape') {
        tapePointsRef.current = [...tapePointsRef.current, worldPos];
        tapeTempPointRef.current = null;
      }
      return;
    }

    // Handle prop tool
    if (e.button === 0 && activeTool === 'prop') {
      // Ctrl+Click: force place a new prop (bypass selection/dragging)
      if (e.ctrlKey && propSettings?.selectedProp && onPropPlace) {
        onPropPlace(worldPos.x, worldPos.y);
        return;
      }

      // If props are selected, check for handle clicks or dragging first
      if (selectedPropIds.length > 0) {
        // For multi-selection, check multi-selection handles first
        if (selectedPropIds.length > 1) {
          const multiHandle = findMultiHandleAtPosition(screenX, screenY);
          if (multiHandle) {
            const multiBounds = getMultiSelectionBounds();
            if (multiBounds) {
              activeHandleRef.current = multiHandle;
              handleStartPosRef.current = { x: screenX, y: screenY };
              isMultiTransformActiveRef.current = true;

              // Store initial state for all selected props
              const propsState = new Map<string, { x: number; y: number; scale: number; scaleX: number; scaleY: number; rotation: number }>();
              for (const propId of selectedPropIds) {
                const prop = props.find(p => p.id === propId);
                if (prop) {
                  const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
                  propsState.set(propId, {
                    x: prop.x,
                    y: prop.y,
                    scale: prop.scale,
                    scaleX: propAny.scaleX ?? prop.scale,
                    scaleY: propAny.scaleY ?? prop.scale,
                    rotation: prop.rotation
                  });
                }
              }

              // Calculate world center from bounds
              const worldCenterX = (multiBounds.worldBounds.minX + multiBounds.worldBounds.maxX) / 2;
              const worldCenterY = (multiBounds.worldBounds.minY + multiBounds.worldBounds.maxY) / 2;

              // Calculate initial distance from center to mouse
              const initialDistance = Math.sqrt(
                Math.pow(screenX - multiBounds.center.x, 2) +
                Math.pow(screenY - multiBounds.center.y, 2)
              );

              multiTransformStartRef.current = {
                props: propsState,
                center: multiBounds.center,
                worldCenter: { x: worldCenterX, y: worldCenterY },
                boundingBox: {
                  width: multiBounds.worldBounds.maxX - multiBounds.worldBounds.minX,
                  height: multiBounds.worldBounds.maxY - multiBounds.worldBounds.minY
                },
                initialDistance
              };

              return;
            }
          }
        }

        // For single selection, check handle manipulation
        if (selectedPropIds.length === 1) {
          const selectedProp = props.find(p => p.id === selectedPropIds[0]);
          if (selectedProp) {
            // Check if clicking on a handle
            const handle = findHandleAtPosition(screenX, screenY, selectedProp);
            if (handle) {
              activeHandleRef.current = handle;
              handleStartPosRef.current = { x: screenX, y: screenY };
              handleStartScaleRef.current = selectedProp.scale;
              const propAny = selectedProp as PropInstance & { scaleX?: number; scaleY?: number };
              handleStartScaleXRef.current = propAny.scaleX ?? selectedProp.scale;
              handleStartScaleYRef.current = propAny.scaleY ?? selectedProp.scale;
              handleStartRotationRef.current = selectedProp.rotation;
              handlePropCenterRef.current = { x: selectedProp.x, y: selectedProp.y };
              return;
            }
          }
        }

        // Check if clicking on any selected prop (for dragging)
        // First check selected props directly (regardless of layer) for dragging
        let clickedSelectedProp: PropInstance | null = null;
        for (const propId of selectedPropIds) {
          const prop = props.find(p => p.id === propId);
          if (prop && propImages) {
            const img = propImages.get(prop.propName);
            if (img) {
              const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
              const scaleX = Math.abs(propAny.scaleX ?? prop.scale);
              const scaleY = Math.abs(propAny.scaleY ?? prop.scale);
              const halfWidth = (img.width * scaleX) / 2;
              const halfHeight = (img.height * scaleY) / 2;
              if (worldPos.x >= prop.x - halfWidth && worldPos.x <= prop.x + halfWidth &&
                  worldPos.y >= prop.y - halfHeight && worldPos.y <= prop.y + halfHeight) {
                clickedSelectedProp = prop;
                break;
              }
            }
          }
        }

        if (clickedSelectedProp) {
          // Start dragging all selected props
          isDraggingPropRef.current = true;
          dragStartPosRef.current = worldPos;

          // Store initial positions of all selected props
          multiDragStartRef.current.clear();
          for (const propId of selectedPropIds) {
            const prop = props.find(p => p.id === propId);
            if (prop) {
              multiDragStartRef.current.set(propId, { x: prop.x, y: prop.y });
            }
          }
          return;
        }

        // Then check for clicking on any prop on the active layer
        const clickedProp = findPropAtPosition(worldPos.x, worldPos.y);

        // Clicked on a non-selected prop - wait to see if it's a click or drag
        if (clickedProp) {
          // Store as pending - will decide on mouseMove/mouseUp if it's select or selection box
          pendingPropClickRef.current = { prop: clickedProp, screenPos: { x: screenX, y: screenY } };
          return;
        }

        // Clicked on empty space - start selection box (don't deselect yet, wait for mouse up)
        isSelectionBoxActiveRef.current = true;
        selectionBoxStartRef.current = worldPos;
        selectionBoxEndRef.current = worldPos;
        return;
      }

      // No props selected
      const clickedProp = findPropAtPosition(worldPos.x, worldPos.y);
      if (clickedProp) {
        // Click on a prop - wait to see if it's a click or drag
        // Store as pending - will decide on mouseMove/mouseUp if it's select or selection box
        pendingPropClickRef.current = { prop: clickedProp, screenPos: { x: screenX, y: screenY } };
        return;
      }

      // Clicked on empty space - start selection box or prepare to place prop
      isSelectionBoxActiveRef.current = true;
      selectionBoxStartRef.current = worldPos;
      selectionBoxEndRef.current = worldPos;
      return;
    }

    // Handle text tool
    if (e.button === 0 && activeTool === 'text') {
      // Check for handle manipulation on selected text (single selection only)
      if (selectedTextIds.length === 1) {
        const selectedText = texts.find(t => t.id === selectedTextIds[0]);
        if (selectedText) {
          const handle = findTextHandleAtPosition(screenX, screenY, selectedText);
          if (handle) {
            activeTextHandleRef.current = handle;
            textHandleStartPosRef.current = { x: screenX, y: screenY };
            textHandleStartSizeRef.current = { width: selectedText.width, height: selectedText.height };
            textHandleStartRotationRef.current = selectedText.rotation;
            textHandleStartCenterRef.current = { x: selectedText.x, y: selectedText.y };
            return;
          }
        }
      }

      // First check selected texts directly (regardless of layer) for dragging
      let clickedSelectedText: TextInstance | null = null;
      for (const textId of selectedTextIds) {
        const text = texts.find(t => t.id === textId);
        if (text) {
          const halfWidth = text.width / 2;
          const halfHeight = text.height / 2;
          if (worldPos.x >= text.x - halfWidth && worldPos.x <= text.x + halfWidth &&
              worldPos.y >= text.y - halfHeight && worldPos.y <= text.y + halfHeight) {
            clickedSelectedText = text;
            break;
          }
        }
      }

      if (clickedSelectedText) {
        // Start dragging all selected texts
        isDraggingTextRef.current = true;
        textDragStartPosRef.current = worldPos;

        // Store original positions for all selected texts
        textDragOriginalPositionsRef.current.clear();
        for (const textId of selectedTextIds) {
          const text = texts.find(t => t.id === textId);
          if (text) {
            textDragOriginalPositionsRef.current.set(textId, { x: text.x, y: text.y });
          }
        }
        return;
      }

      // Check if clicking on an existing text on active layer
      const clickedText = findTextAtPosition(worldPos.x, worldPos.y);

      if (clickedText) {
        // Select the text (wait to see if it's a click or drag)
        onTextSelect?.([clickedText.id]);
        return;
      }

      // No text clicked - place new text if no texts are selected
      if (selectedTextIds.length === 0 && onTextPlace) {
        onTextPlace(worldPos.x, worldPos.y);
        return;
      }

      // Deselect all texts
      onTextSelect?.([]);
      return;
    }

    // Handle pen tool
    if (e.button === 0 && activeTool === 'pen') {
      // Check if currently drawing a path
      if (isDrawingPath && currentPathPoints.length > 0) {
        // Check if clicking near the first point to close the path
        const firstPoint = currentPathPoints[0];
        const distToFirst = Math.sqrt(
          Math.pow(worldPos.x - firstPoint.x, 2) +
          Math.pow(worldPos.y - firstPoint.y, 2)
        );

        // Close path if clicking within 15 screen pixels of first point
        const closeThreshold = 15 / viewportRef.current.zoom;
        if (distToFirst < closeThreshold && currentPathPoints.length >= 2) {
          onPenPathPlace?.(currentPathPoints, true);
          onDrawingPathChange?.(false);
          tempHandleRef.current = null;
          return;
        }

        // Otherwise add a new point
        const newPoint: AnchorPoint = {
          x: worldPos.x,
          y: worldPos.y,
          handleIn: null,
          handleOut: null,
          handleMode: 'smooth'
        };

        // Start handle drag
        isDraggingPenHandleRef.current = true;
        penDragStartPosRef.current = worldPos;

        onCurrentPathUpdate?.([...currentPathPoints, newPoint]);
        return;
      }

      // First check if clicking on rotation handle for selected paths
      if (selectedPenPathIds.length > 0 && !isDrawingPath && activeTool === 'pen') {
        if (findPenPathRotationHandleAtPosition(screenX, screenY)) {
          // Calculate center of all selected paths
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const pathId of selectedPenPathIds) {
            const path = penPaths.find(p => p.id === pathId);
            if (!path) continue;
            for (const point of path.points) {
              minX = Math.min(minX, point.x);
              minY = Math.min(minY, point.y);
              maxX = Math.max(maxX, point.x);
              maxY = Math.max(maxY, point.y);
            }
          }
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          penPathCenterRef.current = { x: centerX, y: centerY };

          // Store original points for all selected paths
          penPathOriginalPointsRef.current.clear();
          for (const pathId of selectedPenPathIds) {
            const path = penPaths.find(p => p.id === pathId);
            if (path) {
              penPathOriginalPointsRef.current.set(pathId, path.points.map(p => ({ ...p })));
            }
          }

          // Calculate initial angle from center to mouse
          const dx = worldPos.x - centerX;
          const dy = worldPos.y - centerY;
          penPathRotationStartAngleRef.current = Math.atan2(dy, dx);

          isRotatingPenPathRef.current = true;
          return;
        }
      }

      // Check if clicking on an anchor point or handle of a selected path
      if (selectedPenPathIds.length > 0 && !isDrawingPath) {
        const hitResult = findAnchorOrHandleAtPosition(worldPos.x, worldPos.y);
        if (hitResult) {
          const path = penPaths.find(p => p.id === hitResult.pathId);
          if (path) {
            penDragStartPosRef.current = worldPos;

            if (hitResult.type === 'anchor') {
              // Check if this anchor is already selected
              const currentSelection = selectedAnchorIndices.get(hitResult.pathId);
              const isAlreadySelected = currentSelection?.has(hitResult.pointIndex) ?? false;
              const anchorPoint = path.points[hitResult.pointIndex];

              // Alt+Click on anchor: start creating handles (convert corner to smooth)
              if (e.altKey) {
                // Select this anchor
                const newSelection = new Map<string, Set<number>>();
                newSelection.set(hitResult.pathId, new Set([hitResult.pointIndex]));
                onAnchorSelect?.(newSelection);

                // Start handle creation mode
                isCreatingInsertedPointHandleRef.current = true;
                insertedPointInfoRef.current = { pathId: hitResult.pathId, pointIndex: hitResult.pointIndex };

                // If point already has handles, clear them first so drag starts fresh
                if (anchorPoint.handleIn || anchorPoint.handleOut) {
                  const newPoints = [...path.points];
                  newPoints[hitResult.pointIndex] = {
                    ...newPoints[hitResult.pointIndex],
                    handleIn: null,
                    handleOut: null,
                    handleMode: 'smooth'
                  };
                  onPenPathPointsUpdate?.(hitResult.pathId, newPoints);
                }
                return;
              } else if (e.shiftKey) {
                // Shift+click: toggle selection
                const newSelection = new Map(selectedAnchorIndices);
                const pathSelection = new Set(currentSelection ?? []);
                if (isAlreadySelected) {
                  pathSelection.delete(hitResult.pointIndex);
                } else {
                  pathSelection.add(hitResult.pointIndex);
                }
                if (pathSelection.size > 0) {
                  newSelection.set(hitResult.pathId, pathSelection);
                } else {
                  newSelection.delete(hitResult.pathId);
                }
                onAnchorSelect?.(newSelection);
                return;
              } else if (isAlreadySelected && getTotalSelectedAnchors() > 1) {
                // Clicking on an already selected anchor with multiple anchors selected: start multi-drag
                isDraggingMultiAnchorsRef.current = true;
                multiAnchorOriginalPositionsRef.current.clear();
                for (const [pathId, indices] of selectedAnchorIndices) {
                  const p = penPaths.find(pp => pp.id === pathId);
                  if (p) {
                    const posMap = new Map<number, { x: number; y: number }>();
                    for (const idx of indices) {
                      if (idx < p.points.length) {
                        posMap.set(idx, { x: p.points[idx].x, y: p.points[idx].y });
                      }
                    }
                    multiAnchorOriginalPositionsRef.current.set(pathId, posMap);
                  }
                }
                return;
              } else {
                // Regular click: select only this anchor
                const newSelection = new Map<string, Set<number>>();
                newSelection.set(hitResult.pathId, new Set([hitResult.pointIndex]));
                onAnchorSelect?.(newSelection);
                // Start single anchor drag
                originalPointRef.current = { ...path.points[hitResult.pointIndex] };
                editingAnchorRef.current = { pathId: hitResult.pathId, pointIndex: hitResult.pointIndex };
                return;
              }
            } else {
              // Handle click - clear anchor selection and edit handle
              onAnchorSelect?.(new Map());
              originalPointRef.current = { ...path.points[hitResult.pointIndex] };
              editingHandleRef.current = {
                pathId: hitResult.pathId,
                pointIndex: hitResult.pointIndex,
                handleType: hitResult.type === 'handleIn' ? 'in' : 'out'
              };
              return;
            }
          }
        }

        // Check if clicking on a path segment (for adding a new point)
        if (e.altKey) {
          const segmentHit = findPathSegmentAtPosition(worldPos.x, worldPos.y);
          if (segmentHit && onPenPathPointsUpdate) {
            const path = penPaths.find(p => p.id === segmentHit.pathId);
            if (path) {
              // Insert a new point at the clicked position
              const newPoints = [...path.points];
              const insertIndex = segmentHit.segmentIndex + 1;

              // Create new anchor point at the curve position
              const newAnchor: AnchorPoint = {
                x: segmentHit.point.x,
                y: segmentHit.point.y,
                handleIn: null,
                handleOut: null,
                handleMode: 'smooth'
              };

              newPoints.splice(insertIndex, 0, newAnchor);
              onPenPathPointsUpdate(segmentHit.pathId, newPoints);

              // Select the new point
              const newSelection = new Map<string, Set<number>>();
              newSelection.set(segmentHit.pathId, new Set([insertIndex]));
              onAnchorSelect?.(newSelection);

              // Start handle creation mode (drag to create smooth handles)
              isCreatingInsertedPointHandleRef.current = true;
              insertedPointInfoRef.current = { pathId: segmentHit.pathId, pointIndex: insertIndex };
              penDragStartPosRef.current = worldPos;
              return;
            }
          }
        }
      }

      // Helper to count total selected anchors
      function getTotalSelectedAnchors(): number {
        let total = 0;
        for (const indices of selectedAnchorIndices.values()) {
          total += indices.size;
        }
        return total;
      }

      // First check selected paths directly (regardless of layer) for dragging
      let clickedSelectedPath: PenPathInstance | null = null;
      for (const pathId of selectedPenPathIds) {
        const path = penPaths.find(p => p.id === pathId);
        if (path && path.points.length > 0) {
          // Calculate bounding box from points
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const point of path.points) {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
          }
          // Add padding based on stroke width
          const padding = (path.strokeWidth || 2) / 2 + 5;
          minX -= padding;
          minY -= padding;
          maxX += padding;
          maxY += padding;

          if (worldPos.x >= minX && worldPos.x <= maxX &&
              worldPos.y >= minY && worldPos.y <= maxY) {
            clickedSelectedPath = path;
            break;
          }
        }
      }

      if (clickedSelectedPath) {
        // Start dragging all selected paths
        isDraggingPenPathRef.current = true;
        penDragStartPosRef.current = worldPos;

        // Notify drag start so MapEditor can store original points
        onPenPathDragStart?.(selectedPenPathIds);
        return;
      }

      // Check if clicking on an existing path on active layer
      const clickedPath = findPenPathAtPosition(worldPos.x, worldPos.y);
      if (clickedPath) {
        // Select the path
        onPenPathSelect?.([clickedPath.id]);
        return;
      }

      // No path clicked and not drawing - start a new path
      if (!isDrawingPath && selectedPenPathIds.length === 0) {
        const newPoint: AnchorPoint = {
          x: worldPos.x,
          y: worldPos.y,
          handleIn: null,
          handleOut: null,
          handleMode: 'smooth'
        };

        onDrawingPathChange?.(true);
        onCurrentPathUpdate?.([newPoint]);

        // Start handle drag
        isDraggingPenHandleRef.current = true;
        penDragStartPosRef.current = worldPos;
        return;
      }

      // Deselect paths if clicking empty space
      if (selectedPenPathIds.length > 0) {
        onPenPathSelect?.([]);
      }
      return;
    }

    // Handle shape tool
    if (e.button === 0 && activeTool === 'shape') {
      // If shapes are selected, check for handle clicks or dragging first
      if (selectedShapeIds.length > 0) {
        const selectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
        if (selectedShape) {
          // Get all handles - use CSS dimensions (rect.width/height), not pixel dimensions (canvas.width/height)
          const canvasCenterX = rect ? rect.width / 2 : 0;
          const canvasCenterY = rect ? rect.height / 2 : 0;
          const handles = getShapeHandles(selectedShape, viewport.zoom, viewport.x, viewport.y, canvasCenterX, canvasCenterY, true);

          if (handles && handles.length > 0) {
            // Check rotation handle
            const rotateHandle = handles.find(h => h.type === 'rotate');
            if (rotateHandle) {
              const rotateDistSq = Math.pow(screenX - rotateHandle.x, 2) + Math.pow(screenY - rotateHandle.y, 2);
              if (rotateDistSq < 100) { // 10px radius
                isRotatingShapeRef.current = true;
                shapeOriginalRotationRef.current = selectedShape.rotation;
                shapeCenterRef.current = { x: selectedShape.x, y: selectedShape.y };
                const dx = worldPos.x - selectedShape.x;
                const dy = worldPos.y - selectedShape.y;
                shapeRotationStartAngleRef.current = Math.atan2(dy, dx);
                return;
              }
            }

            // Check corner resize handles
            const cornerHandles = handles.filter(h => h.type === 'resize-corner');
            for (const cornerHandle of cornerHandles) {
              const distSq = Math.pow(screenX - cornerHandle.x, 2) + Math.pow(screenY - cornerHandle.y, 2);
              if (distSq < 64) { // 8px radius
                activeShapeHandleRef.current = cornerHandle.position as ShapeHandle;
                shapeHandleStartRef.current = worldPos;
                shapeOriginalParamsRef.current = JSON.parse(JSON.stringify(selectedShape.shapeParams));
                shapeOriginalPosRef.current = { x: selectedShape.x, y: selectedShape.y };
                return;
              }
            }

            // Check edge resize handles
            const edgeHandles = handles.filter(h => h.type === 'resize-edge');
            for (const edgeHandle of edgeHandles) {
              const distSq = Math.pow(screenX - edgeHandle.x, 2) + Math.pow(screenY - edgeHandle.y, 2);
              if (distSq < 64) { // 8px radius
                activeShapeHandleRef.current = edgeHandle.position as ShapeHandle;
                shapeHandleStartRef.current = worldPos;
                shapeOriginalParamsRef.current = JSON.parse(JSON.stringify(selectedShape.shapeParams));
                shapeOriginalPosRef.current = { x: selectedShape.x, y: selectedShape.y };
                return;
              }
            }

            // Check corner radius handles for rectangles
            if (selectedShape.shapeType === 'rectangle') {
              const crHandles = handles.filter(h => h.type === 'corner-radius');
              for (const crHandle of crHandles) {
                const distSq = Math.pow(screenX - crHandle.x, 2) + Math.pow(screenY - crHandle.y, 2);
                if (distSq < 64) {
                  isDraggingCornerRadiusRef.current = true;
                  const rectParams = selectedShape.shapeParams.params as { cornerRadius: { topLeft: number } };
                  cornerRadiusStartValueRef.current = rectParams.cornerRadius.topLeft;
                  cornerRadiusStartPosRef.current = worldPos;
                  return;
                }
              }
            }

            // Check star inner radius handle
            if (selectedShape.shapeType === 'star') {
              const starInnerHandle = handles.find(h => h.type === 'star-inner');
              if (starInnerHandle) {
                const distSq = Math.pow(screenX - starInnerHandle.x, 2) + Math.pow(screenY - starInnerHandle.y, 2);
                if (distSq < 64) {
                  isDraggingStarInnerRef.current = true;
                  const starParams = selectedShape.shapeParams.params as { innerRatio: number };
                  starInnerStartRatioRef.current = starParams.innerRatio;
                  starInnerStartPosRef.current = worldPos;
                  return;
                }
              }
            }
          }
        }

        // Check if clicking on any selected shape (for dragging)
        let clickedSelectedShape: ShapeInstance | null = null;
        for (const shapeId of selectedShapeIds) {
          const shape = shapes.find(s => s.id === shapeId);
          if (shape && isPointInShape(shape, worldPos.x, worldPos.y)) {
            clickedSelectedShape = shape;
            break;
          }
        }

        if (clickedSelectedShape) {
          // Start dragging all selected shapes
          isDraggingShapeRef.current = true;
          shapeDragStartRef.current = worldPos;
          shapeDragOriginalPosRef.current.clear();
          for (const shapeId of selectedShapeIds) {
            const shape = shapes.find(s => s.id === shapeId);
            if (shape) {
              shapeDragOriginalPosRef.current.set(shapeId, { x: shape.x, y: shape.y });
            }
          }
          return;
        }
      }

      // Check if clicking on an existing shape on active layer
      const clickedShape = shapes.find(s => {
        const shapeLayer = s.layerId || 'default';
        return shapeLayer === activeLayerId && isPointInShape(s, worldPos.x, worldPos.y);
      });

      if (clickedShape) {
        // Select the shape
        onShapeSelect?.([clickedShape.id]);
        return;
      }

      // No shape clicked and none selected - start drawing a new shape
      if (selectedShapeIds.length === 0 && shapeSettings) {
        shapeDrawStartRef.current = worldPos;
        shapeDrawCurrentRef.current = worldPos;
        onDrawingShapeChange?.(true);
        return;
      }

      // Deselect shapes if clicking empty space
      if (selectedShapeIds.length > 0) {
        onShapeSelect?.([]);
      }
      return;
    }

    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      // Middle click or Alt+Left click: always start panning
      didPanRef.current = false;
      setIsPanning(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (e.button === 0 && activeTool === 'pan') {
      // Pan tool with left click
      didPanRef.current = false;
      setIsPanning(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (e.button === 0 && activeTool === 'brush' && brushSettings.brushName && brushSettings.materialName) {
      // Brush tool with left click
      // Set initial rotation based on mode
      if (brushSettings.rotationMode === 'stroke') {
        strokeRotationRef.current = Math.random() * 360;
        setPreviewRotation(Math.random() * 360); // Update preview for next stroke
      } else if (brushSettings.rotationMode === 'continuous') {
        strokeRotationRef.current = Math.random() * 360;
        // Start continuous rotation interval
        continuousRotationIntervalRef.current = setInterval(() => {
          strokeRotationRef.current = Math.random() * 360;
          setPreviewRotation(strokeRotationRef.current);
        }, 50);
      } else {
        strokeRotationRef.current = brushSettings.rotation;
      }

      setIsDrawingActive(true);
      const worldPos2 = screenToWorld(e.clientX, e.clientY);

      // Preload tiles before drawing to avoid overwriting existing content
      if (tileManager) {
        tileManager.ensureBrushTilesLoaded(worldPos2.x, worldPos2.y, brushSettings.size).then(() => {
          drawStroke(worldPos2.x, worldPos2.y);
          setLastDrawPos(worldPos2);
        });
      }
    } else if (e.button === 0 && activeTool === 'eraser' && brushSettings.brushName) {
      // Eraser tool with left click
      // Set initial rotation based on mode
      if (brushSettings.rotationMode === 'stroke') {
        strokeRotationRef.current = Math.random() * 360;
        setPreviewRotation(Math.random() * 360); // Update preview for next stroke
      } else if (brushSettings.rotationMode === 'continuous') {
        strokeRotationRef.current = Math.random() * 360;
        // Start continuous rotation interval
        continuousRotationIntervalRef.current = setInterval(() => {
          strokeRotationRef.current = Math.random() * 360;
          setPreviewRotation(strokeRotationRef.current);
        }, 50);
      } else {
        strokeRotationRef.current = brushSettings.rotation;
      }

      setIsDrawingActive(true);
      const worldPos2 = screenToWorld(e.clientX, e.clientY);

      // Preload tiles before erasing to avoid overwriting existing content
      if (tileManager) {
        tileManager.ensureBrushTilesLoaded(worldPos2.x, worldPos2.y, brushSettings.size).then(() => {
          drawStroke(worldPos2.x, worldPos2.y);
          setLastDrawPos(worldPos2);
        });
      }
    }
  }, [activeTool, activeMeasureTool, brushSettings, screenToWorld, drawStroke, findPropAtPosition, findTextAtPosition, findHandleAtPosition, findMultiHandleAtPosition, findTextHandleAtPosition, getMultiSelectionBounds, onPropSelect, onPropPlace, onTextSelect, onTextPlace, selectedPropIds, selectedTextIds, props, texts, propSettings, tileManager, findPenPathRotationHandleAtPosition, findAnchorOrHandleAtPosition, penPaths, selectedPenPathIds, shapes, selectedShapeIds, shapeSettings, onShapeSelect, onDrawingShapeChange, viewport, activeLayerId, onLayerElementClick]);

  /**
   * Handles mouse move for panning or continuous drawing
   */
  const handleMouseMove = useCallback((e: MouseEvent<HTMLCanvasElement>): void => {
    const currentPos = { x: e.clientX, y: e.clientY };

    // Update mouse world position for display
    const worldPos = screenToWorld(e.clientX, e.clientY);
    setMouseWorldPos(worldPos);

    // Handle measurement tools
    if (activeMeasureTool === 'ruler' && isRulerActiveRef.current) {
      rulerEndRef.current = worldPos;
    } else if (activeMeasureTool === 'tape' && tapePointsRef.current.length > 0) {
      tapeTempPointRef.current = worldPos;
    }

    // Handle selection box dragging
    if (isSelectionBoxActiveRef.current && selectionBoxStartRef.current) {
      selectionBoxEndRef.current = worldPos;
    }

    // Handle pending prop click - if mouse moved far enough, start selection box instead of selecting prop
    if (pendingPropClickRef.current) {
      const dx = e.clientX - pendingPropClickRef.current.screenPos.x;
      const dy = e.clientY - pendingPropClickRef.current.screenPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > DRAG_THRESHOLD) {
        // Mouse moved enough - start selection box from the original click position
        const startWorldPos = screenToWorld(
          pendingPropClickRef.current.screenPos.x,
          pendingPropClickRef.current.screenPos.y
        );
        isSelectionBoxActiveRef.current = true;
        selectionBoxStartRef.current = startWorldPos;
        selectionBoxEndRef.current = worldPos;
        pendingPropClickRef.current = null;
      }
    }

    // Handle multi-prop dragging
    if (isDraggingPropRef.current && dragStartPosRef.current && onPropMove && selectedPropIds.length > 0) {
      // Update shift state
      isShiftHeldRef.current = e.shiftKey;

      // Calculate delta from drag start
      let deltaX = worldPos.x - dragStartPosRef.current.x;
      let deltaY = worldPos.y - dragStartPosRef.current.y;

      // Smart edge snap when Shift is held (only for single prop for simplicity)
      if (isShiftHeldRef.current && selectedPropIds.length === 1) {
        const propId = selectedPropIds[0];
        const startPos = multiDragStartRef.current.get(propId);
        const prop = props.find(p => p.id === propId);
        if (startPos && prop && propImages) {
          const img = propImages.get(prop.propName);
          if (img) {
            const gridSize = scale;
            const snapThreshold = gridSize * 0.15;
            // Use absolute values for dimensions since negative scale is for flipping
            const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
            const scaleXVal = Math.abs(propAny.scaleX ?? prop.scale);
            const scaleYVal = Math.abs(propAny.scaleY ?? prop.scale);
            const halfWidth = (img.width * scaleXVal) / 2;
            const halfHeight = (img.height * scaleYVal) / 2;

            // Calculate new position
            let newX = startPos.x + deltaX;
            let newY = startPos.y + deltaY;

            // Calculate edges
            const leftEdge = newX - halfWidth;
            const rightEdge = newX + halfWidth;
            const topEdge = newY - halfHeight;
            const bottomEdge = newY + halfHeight;

            // Find nearest grid lines
            const nearestLeftGrid = Math.round(leftEdge / gridSize) * gridSize;
            const nearestRightGrid = Math.round(rightEdge / gridSize) * gridSize;
            const nearestTopGrid = Math.round(topEdge / gridSize) * gridSize;
            const nearestBottomGrid = Math.round(bottomEdge / gridSize) * gridSize;

            const leftDist = Math.abs(leftEdge - nearestLeftGrid);
            const rightDist = Math.abs(rightEdge - nearestRightGrid);
            const topDist = Math.abs(topEdge - nearestTopGrid);
            const bottomDist = Math.abs(bottomEdge - nearestBottomGrid);

            // Snap X
            if (leftDist < rightDist && leftDist < snapThreshold) {
              deltaX = nearestLeftGrid + halfWidth - startPos.x;
            } else if (rightDist < snapThreshold) {
              deltaX = nearestRightGrid - halfWidth - startPos.x;
            }

            // Snap Y
            if (topDist < bottomDist && topDist < snapThreshold) {
              deltaY = nearestTopGrid + halfHeight - startPos.y;
            } else if (bottomDist < snapThreshold) {
              deltaY = nearestBottomGrid - halfHeight - startPos.y;
            }
          }
        }
      }

      // Calculate new absolute positions for all props
      const newPositions = new Map<string, { x: number; y: number }>();
      for (const propId of selectedPropIds) {
        const startPos = multiDragStartRef.current.get(propId);
        if (startPos) {
          newPositions.set(propId, {
            x: startPos.x + deltaX,
            y: startPos.y + deltaY
          });
        }
      }

      // Move all selected props
      onPropMove(newPositions);
    }

    // Handle text dragging
    if (isDraggingTextRef.current && textDragStartPosRef.current && onTextMove && selectedTextIds.length > 0) {
      const deltaX = worldPos.x - textDragStartPosRef.current.x;
      const deltaY = worldPos.y - textDragStartPosRef.current.y;

      const newPositions = new Map<string, { x: number; y: number }>();
      for (const textId of selectedTextIds) {
        const originalPos = textDragOriginalPositionsRef.current.get(textId);
        if (originalPos) {
          newPositions.set(textId, {
            x: originalPos.x + deltaX,
            y: originalPos.y + deltaY
          });
        }
      }

      onTextMove(newPositions);
    }

    // Handle text handle manipulation (resize/rotate)
    if (activeTextHandleRef.current && textHandleStartPosRef.current && textHandleStartSizeRef.current && textHandleStartCenterRef.current && onTextTransform && selectedTextIds.length === 1) {
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const screenX = rect ? e.clientX - rect.left : 0;
      const screenY = rect ? e.clientY - rect.top : 0;

      const selectedText = texts.find(t => t.id === selectedTextIds[0]);
      if (selectedText) {
        if (activeTextHandleRef.current === 'rotate') {
          // Calculate rotation based on angle from center
          const centerScreenX = (textHandleStartCenterRef.current.x - viewportRef.current.x) * viewportRef.current.zoom + (rect?.width || 0) / 2;
          const centerScreenY = (textHandleStartCenterRef.current.y - viewportRef.current.y) * viewportRef.current.zoom + (rect?.height || 0) / 2;

          const startAngle = Math.atan2(
            textHandleStartPosRef.current.y - centerScreenY,
            textHandleStartPosRef.current.x - centerScreenX
          );
          const currentAngle = Math.atan2(
            screenY - centerScreenY,
            screenX - centerScreenX
          );
          const angleDelta = (currentAngle - startAngle) * (180 / Math.PI);
          let newRotation = textHandleStartRotationRef.current + angleDelta;

          // Snap to 15 degree increments if shift is held
          if (e.shiftKey) {
            newRotation = Math.round(newRotation / 15) * 15;
          }

          onTextTransform(selectedTextIds, selectedText.width, selectedText.height, newRotation);
        } else {
          // Resize handle - allow free resize (width and height independently)
          const centerScreenX = (textHandleStartCenterRef.current.x - viewportRef.current.x) * viewportRef.current.zoom + (rect?.width || 0) / 2;
          const centerScreenY = (textHandleStartCenterRef.current.y - viewportRef.current.y) * viewportRef.current.zoom + (rect?.height || 0) / 2;

          // Apply rotation to get distances in local coordinates
          const rad = -(selectedText.rotation * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);

          // Calculate start offset from center in rotated space
          const startOffsetX = textHandleStartPosRef.current.x - centerScreenX;
          const startOffsetY = textHandleStartPosRef.current.y - centerScreenY;
          const startLocalX = Math.abs(startOffsetX * cos - startOffsetY * sin);
          const startLocalY = Math.abs(startOffsetX * sin + startOffsetY * cos);

          // Calculate current offset from center in rotated space
          const currentOffsetX = screenX - centerScreenX;
          const currentOffsetY = screenY - centerScreenY;
          const currentLocalX = Math.abs(currentOffsetX * cos - currentOffsetY * sin);
          const currentLocalY = Math.abs(currentOffsetX * sin + currentOffsetY * cos);

          // Calculate scale factors for each dimension
          const scaleFactorX = startLocalX > 0 ? currentLocalX / startLocalX : 1;
          const scaleFactorY = startLocalY > 0 ? currentLocalY / startLocalY : 1;

          // Apply scale to width/height independently
          let newWidth = textHandleStartSizeRef.current.width * scaleFactorX;
          let newHeight = textHandleStartSizeRef.current.height * scaleFactorY;

          // Minimum size constraints
          newWidth = Math.max(50, newWidth);
          newHeight = Math.max(20, newHeight);

          // If shift is held, maintain aspect ratio (use uniform scale)
          if (e.shiftKey) {
            const uniformScale = Math.max(scaleFactorX, scaleFactorY);
            newWidth = Math.max(50, textHandleStartSizeRef.current.width * uniformScale);
            newHeight = Math.max(20, textHandleStartSizeRef.current.height * uniformScale);
          }

          onTextTransform(selectedTextIds, newWidth, newHeight, selectedText.rotation);
        }
      }
    }

    // Handle multi-prop transform (resize/rotate for multi-selection)
    if (isMultiTransformActiveRef.current && activeHandleRef.current && multiTransformStartRef.current && handleStartPosRef.current && onPropMove && onPropTransform) {
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const screenX = rect ? e.clientX - rect.left : 0;
      const screenY = rect ? e.clientY - rect.top : 0;

      const startData = multiTransformStartRef.current;

      if (activeHandleRef.current === 'rotate') {
        // Calculate rotation delta
        const startAngle = Math.atan2(
          handleStartPosRef.current.y - startData.center.y,
          handleStartPosRef.current.x - startData.center.x
        );
        const currentAngle = Math.atan2(
          screenY - startData.center.y,
          screenX - startData.center.x
        );
        const deltaAngle = currentAngle - startAngle;

        // Apply rotation to all props: rotate positions around center + add rotation delta
        const newPositions = new Map<string, { x: number; y: number }>();
        const cos = Math.cos(deltaAngle);
        const sin = Math.sin(deltaAngle);

        for (const [propId, initial] of startData.props.entries()) {
          // Rotate position around world center
          const relX = initial.x - startData.worldCenter.x;
          const relY = initial.y - startData.worldCenter.y;
          const newX = startData.worldCenter.x + relX * cos - relY * sin;
          const newY = startData.worldCenter.y + relX * sin + relY * cos;

          newPositions.set(propId, { x: newX, y: newY });

          // Apply rotation delta to prop's own rotation
          let newRotation = (initial.rotation + (deltaAngle * 180) / Math.PI) % 360;
          if (newRotation < 0) newRotation += 360;

          onPropTransform([propId], initial.scale, initial.scaleX, initial.scaleY, newRotation);
        }

        onPropMove(newPositions);
      } else {
        // Scale handles (tl, tr, bl, br)
        const currentDist = Math.sqrt(
          Math.pow(screenX - startData.center.x, 2) +
          Math.pow(screenY - startData.center.y, 2)
        );

        if (startData.initialDistance > 0) {
          // Calculate vectors from center
          const startVecX = handleStartPosRef.current.x - startData.center.x;
          const startVecY = handleStartPosRef.current.y - startData.center.y;
          const currentVecX = screenX - startData.center.x;
          const currentVecY = screenY - startData.center.y;

          // Dot product to detect if we crossed the center (for flipping)
          const dotProduct = startVecX * currentVecX + startVecY * currentVecY;
          const isFlipped = dotProduct < 0;

          const scaleFactor = (currentDist / startData.initialDistance) * (isFlipped ? -1 : 1);

          // Update Ctrl key state for non-uniform scaling
          isCtrlHeldRef.current = e.ctrlKey;

          // Calculate X and Y scale factors for non-uniform scaling
          const dx = screenX - startData.center.x;
          const dy = screenY - startData.center.y;
          const startDx = handleStartPosRef.current.x - startData.center.x;
          const startDy = handleStartPosRef.current.y - startData.center.y;
          const scaleFactorX = Math.abs(startDx) > 5 ? dx / startDx : 1;
          const scaleFactorY = Math.abs(startDy) > 5 ? dy / startDy : 1;

          // Apply scale to all props: scale positions from center + scale each prop
          const newPositions = new Map<string, { x: number; y: number }>();

          for (const [propId, initial] of startData.props.entries()) {
            // Scale position from world center
            const relX = initial.x - startData.worldCenter.x;
            const relY = initial.y - startData.worldCenter.y;

            if (isCtrlHeldRef.current) {
              // Non-uniform scaling: the SELECTION deforms as one object
              // Both positions AND prop scales follow the same X/Y deformation
              const newX = startData.worldCenter.x + relX * scaleFactorX;
              const newY = startData.worldCenter.y + relY * scaleFactorY;
              newPositions.set(propId, { x: newX, y: newY });

              // Apply X deformation to scaleX, Y deformation to scaleY
              // This makes the selection behave like a single flattened/stretched image
              const newScaleX = initial.scaleX * scaleFactorX;
              const newScaleY = initial.scaleY * scaleFactorY;
              onPropTransform([propId], (Math.abs(newScaleX) + Math.abs(newScaleY)) / 2, newScaleX, newScaleY, initial.rotation);
            } else {
              // Uniform/proportional scaling: each prop keeps its original scaleX/scaleY ratio
              const newX = startData.worldCenter.x + relX * scaleFactor;
              const newY = startData.worldCenter.y + relY * scaleFactor;
              newPositions.set(propId, { x: newX, y: newY });

              // Multiply both scaleX and scaleY by the same factor to preserve proportions
              const newScaleX = initial.scaleX * scaleFactor;
              const newScaleY = initial.scaleY * scaleFactor;
              onPropTransform([propId], initial.scale * scaleFactor, newScaleX, newScaleY, initial.rotation);
            }
          }

          onPropMove(newPositions);
        }
      }
    }

    // Handle prop handle manipulation (single selection only)
    if (activeHandleRef.current && !isMultiTransformActiveRef.current && selectedPropIds.length === 1 && handleStartPosRef.current && onPropTransform) {
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const screenX = rect ? e.clientX - rect.left : 0;
      const screenY = rect ? e.clientY - rect.top : 0;

      // Update Ctrl key state
      isCtrlHeldRef.current = e.ctrlKey;

      if (activeHandleRef.current === 'rotate') {
        // Calculate rotation
        const centerScreen = worldToScreen(handlePropCenterRef.current.x, handlePropCenterRef.current.y);
        const startAngle = Math.atan2(
          handleStartPosRef.current.y - centerScreen.y,
          handleStartPosRef.current.x - centerScreen.x
        );
        const currentAngle = Math.atan2(
          screenY - centerScreen.y,
          screenX - centerScreen.x
        );
        const deltaAngle = ((currentAngle - startAngle) * 180) / Math.PI;
        let newRotation = (handleStartRotationRef.current + deltaAngle) % 360;
        if (newRotation < 0) newRotation += 360;

        onPropTransform(
          selectedPropIds,
          handleStartScaleRef.current,
          handleStartScaleXRef.current,
          handleStartScaleYRef.current,
          newRotation
        );
      } else {
        // Scale handles (tl, tr, bl, br)
        const centerScreen = worldToScreen(handlePropCenterRef.current.x, handlePropCenterRef.current.y);

        // Calculate distance from center
        const startDist = Math.sqrt(
          Math.pow(handleStartPosRef.current.x - centerScreen.x, 2) +
          Math.pow(handleStartPosRef.current.y - centerScreen.y, 2)
        );
        const currentDist = Math.sqrt(
          Math.pow(screenX - centerScreen.x, 2) +
          Math.pow(screenY - centerScreen.y, 2)
        );

        if (startDist > 0) {
          // Calculate vectors from center
          const startVecX = handleStartPosRef.current.x - centerScreen.x;
          const startVecY = handleStartPosRef.current.y - centerScreen.y;
          const currentVecX = screenX - centerScreen.x;
          const currentVecY = screenY - centerScreen.y;

          // Dot product to detect if we crossed the center (for flipping)
          const dotProduct = startVecX * currentVecX + startVecY * currentVecY;
          const isFlipped = dotProduct < 0;

          const scaleFactor = (currentDist / startDist) * (isFlipped ? -1 : 1);

          if (isCtrlHeldRef.current) {
            // Non-uniform scaling: calculate X and Y scale separately
            const dx = screenX - centerScreen.x;
            const dy = screenY - centerScreen.y;
            const startDx = handleStartPosRef.current.x - centerScreen.x;
            const startDy = handleStartPosRef.current.y - centerScreen.y;

            // Apply rotation to get local coordinates
            const rad = -(handleStartRotationRef.current * Math.PI) / 180;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);

            const localX = dx * cos - dy * sin;
            const localY = dx * sin + dy * cos;
            const startLocalX = startDx * cos - startDy * sin;
            const startLocalY = startDx * sin + startDy * cos;

            // Allow negative scale factors for flipping/mirroring
            const scaleFactorX = Math.abs(startLocalX) > 5 ? localX / startLocalX : 1;
            const scaleFactorY = Math.abs(startLocalY) > 5 ? localY / startLocalY : 1;

            const newScaleX = handleStartScaleXRef.current * scaleFactorX;
            const newScaleY = handleStartScaleYRef.current * scaleFactorY;

            onPropTransform(
              selectedPropIds,
              (newScaleX + newScaleY) / 2, // Average for uniform scale field
              newScaleX,
              newScaleY,
              handleStartRotationRef.current
            );
          } else {
            // Uniform scaling
            const newScale = handleStartScaleRef.current * scaleFactor;
            onPropTransform(
              selectedPropIds,
              newScale,
              newScale,
              newScale,
              handleStartRotationRef.current
            );
          }
        }
      }
    }

    // Handle pen tool handle dragging (creating curves)
    if (isDraggingPenHandleRef.current && penDragStartPosRef.current && currentPathPoints.length > 0) {
      const lastPointIndex = currentPathPoints.length - 1;
      const lastPoint = currentPathPoints[lastPointIndex];

      // Calculate handle offset from anchor point
      const handleOffset = {
        x: worldPos.x - lastPoint.x,
        y: worldPos.y - lastPoint.y
      };

      // Update tempHandle for preview
      tempHandleRef.current = handleOffset;

      // Use e.altKey directly for more reliable Alt detection during drag
      const altHeld = e.altKey || isAltHeldRef.current;

      // Update the last point's handles
      const updatedPoints = [...currentPathPoints];
      const handleIn = altHeld ? null : { x: -handleOffset.x, y: -handleOffset.y };
      updatedPoints[lastPointIndex] = {
        ...lastPoint,
        handleIn: lastPointIndex > 0 ? handleIn : null, // First point has no handleIn initially
        handleOut: handleOffset,
        handleMode: altHeld ? 'corner' : 'smooth'
      };

      onCurrentPathUpdate?.(updatedPoints);
    }

    // Handle pen path dragging
    if (isDraggingPenPathRef.current && penDragStartPosRef.current && onPenPathMove && selectedPenPathIds.length > 0) {
      const deltaX = worldPos.x - penDragStartPosRef.current.x;
      const deltaY = worldPos.y - penDragStartPosRef.current.y;

      // Pass the delta from drag start to move handler
      onPenPathMove(selectedPenPathIds, deltaX, deltaY);
    }

    // Handle pen path rotation
    if (isRotatingPenPathRef.current && penPathCenterRef.current && onPenPathPointsUpdate) {
      const center = penPathCenterRef.current;
      const currentAngle = Math.atan2(worldPos.y - center.y, worldPos.x - center.x);
      const angleDelta = currentAngle - penPathRotationStartAngleRef.current;

      const cos = Math.cos(angleDelta);
      const sin = Math.sin(angleDelta);

      // Rotate all points around center for each selected path
      for (const pathId of selectedPenPathIds) {
        const originalPoints = penPathOriginalPointsRef.current.get(pathId);
        if (!originalPoints) continue;

        const newPoints: AnchorPoint[] = originalPoints.map(originalPoint => {
          // Rotate anchor point
          const dx = originalPoint.x - center.x;
          const dy = originalPoint.y - center.y;
          const newX = center.x + dx * cos - dy * sin;
          const newY = center.y + dx * sin + dy * cos;

          // Rotate handles (they are relative to anchor, so just rotate them)
          let newHandleIn = originalPoint.handleIn;
          let newHandleOut = originalPoint.handleOut;

          if (originalPoint.handleIn) {
            newHandleIn = {
              x: originalPoint.handleIn.x * cos - originalPoint.handleIn.y * sin,
              y: originalPoint.handleIn.x * sin + originalPoint.handleIn.y * cos
            };
          }
          if (originalPoint.handleOut) {
            newHandleOut = {
              x: originalPoint.handleOut.x * cos - originalPoint.handleOut.y * sin,
              y: originalPoint.handleOut.x * sin + originalPoint.handleOut.y * cos
            };
          }

          return {
            ...originalPoint,
            x: newX,
            y: newY,
            handleIn: newHandleIn,
            handleOut: newHandleOut
          };
        });

        onPenPathPointsUpdate(pathId, newPoints);
      }
    }

    // Handle multi-anchor dragging (moving multiple selected anchors together)
    if (isDraggingMultiAnchorsRef.current && penDragStartPosRef.current && onPenPathPointsUpdate) {
      const deltaX = worldPos.x - penDragStartPosRef.current.x;
      const deltaY = worldPos.y - penDragStartPosRef.current.y;

      for (const [pathId, originalPositions] of multiAnchorOriginalPositionsRef.current) {
        const path = penPaths.find(p => p.id === pathId);
        if (!path) continue;

        const newPoints = [...path.points];
        for (const [idx, origPos] of originalPositions) {
          if (idx < newPoints.length) {
            newPoints[idx] = {
              ...newPoints[idx],
              x: origPos.x + deltaX,
              y: origPos.y + deltaY
            };
          }
        }
        onPenPathPointsUpdate(pathId, newPoints);
      }
    }

    // Handle creating handles for newly inserted point (Alt+Click+Drag)
    if (isCreatingInsertedPointHandleRef.current && insertedPointInfoRef.current && penDragStartPosRef.current && onPenPathPointsUpdate) {
      const { pathId, pointIndex } = insertedPointInfoRef.current;
      const path = penPaths.find(p => p.id === pathId);
      if (path && pointIndex < path.points.length) {
        const point = path.points[pointIndex];

        // Calculate handle offset from anchor point
        const handleOffset = {
          x: worldPos.x - point.x,
          y: worldPos.y - point.y
        };

        // Create symmetric handles (smooth mode)
        const newPoints = [...path.points];
        newPoints[pointIndex] = {
          ...newPoints[pointIndex],
          handleIn: { x: -handleOffset.x, y: -handleOffset.y },
          handleOut: handleOffset,
          handleMode: 'smooth'
        };
        onPenPathPointsUpdate(pathId, newPoints);
      }
    }

    // Handle anchor point dragging for selected paths
    if (editingAnchorRef.current && penDragStartPosRef.current && originalPointRef.current && onPenPathPointsUpdate) {
      const { pathId, pointIndex } = editingAnchorRef.current;
      const path = penPaths.find(p => p.id === pathId);
      if (path) {
        const deltaX = worldPos.x - penDragStartPosRef.current.x;
        const deltaY = worldPos.y - penDragStartPosRef.current.y;

        const newPoints = [...path.points];
        newPoints[pointIndex] = {
          ...newPoints[pointIndex],
          x: originalPointRef.current.x + deltaX,
          y: originalPointRef.current.y + deltaY
        };
        onPenPathPointsUpdate(pathId, newPoints);
      }
    }

    // Handle control handle dragging for selected paths
    if (editingHandleRef.current && penDragStartPosRef.current && originalPointRef.current && onPenPathPointsUpdate) {
      const { pathId, pointIndex, handleType } = editingHandleRef.current;
      const path = penPaths.find(p => p.id === pathId);
      if (path) {
        const point = path.points[pointIndex];
        // Calculate handle offset relative to anchor point
        const handleOffset = {
          x: worldPos.x - point.x,
          y: worldPos.y - point.y
        };

        // Use e.altKey directly for more reliable Alt detection during drag
        const altHeld = e.altKey || isAltHeldRef.current;

        const newPoints = [...path.points];
        const isSmooth = !altHeld && point.handleMode === 'smooth';

        if (handleType === 'out') {
          newPoints[pointIndex] = {
            ...newPoints[pointIndex],
            handleOut: handleOffset,
            handleIn: isSmooth && newPoints[pointIndex].handleIn
              ? { x: -handleOffset.x, y: -handleOffset.y }
              : newPoints[pointIndex].handleIn,
            handleMode: altHeld ? 'corner' : point.handleMode
          };
        } else {
          newPoints[pointIndex] = {
            ...newPoints[pointIndex],
            handleIn: handleOffset,
            handleOut: isSmooth && newPoints[pointIndex].handleOut
              ? { x: -handleOffset.x, y: -handleOffset.y }
              : newPoints[pointIndex].handleOut,
            handleMode: altHeld ? 'corner' : point.handleMode
          };
        }
        onPenPathPointsUpdate(pathId, newPoints);
      }
    }

    if (isPanning && lastMousePos) {
      const dx = (e.clientX - lastMousePos.x) / viewport.zoom;
      const dy = (e.clientY - lastMousePos.y) / viewport.zoom;

      // Mark that we actually panned (moved the viewport)
      if (dx !== 0 || dy !== 0) {
        didPanRef.current = true;
      }

      setViewport((prev) => ({
        ...prev,
        x: prev.x - dx,
        y: prev.y - dy
      }));
    } else if (isDrawingActive && lastDrawPos && brushSettings.brushName) {
      // Continuous drawing/erasing
      const canDraw = activeTool === 'brush' ? brushSettings.materialName : true;
      if (canDraw) {
        drawStrokesAlongLine(lastDrawPos.x, lastDrawPos.y, worldPos.x, worldPos.y);
        setLastDrawPos(worldPos);
      }
    }

    // Handle shape drawing preview
    if (shapeDrawStartRef.current && activeTool === 'shape') {
      shapeDrawCurrentRef.current = worldPos;
      isShiftHeldRef.current = e.shiftKey;
      isAltHeldRef.current = e.altKey;
    }

    // Handle shape dragging
    if (isDraggingShapeRef.current && shapeDragStartRef.current && onShapeMove && selectedShapeIds.length > 0) {
      const deltaX = worldPos.x - shapeDragStartRef.current.x;
      const deltaY = worldPos.y - shapeDragStartRef.current.y;

      // Calculate new absolute positions using original positions
      const newPositions = new Map<string, { x: number; y: number }>();
      for (const shapeId of selectedShapeIds) {
        const originalPos = shapeDragOriginalPosRef.current.get(shapeId);
        if (originalPos) {
          newPositions.set(shapeId, {
            x: originalPos.x + deltaX,
            y: originalPos.y + deltaY
          });
        }
      }
      onShapeMove(newPositions);
    }

    // Handle shape rotation
    if (isRotatingShapeRef.current && shapeCenterRef.current && onShapeTransform && selectedShapeIds.length > 0) {
      const center = shapeCenterRef.current;
      const currentAngle = Math.atan2(worldPos.y - center.y, worldPos.x - center.x);
      const angleDelta = currentAngle - shapeRotationStartAngleRef.current;
      let newRotation = shapeOriginalRotationRef.current + (angleDelta * 180 / Math.PI);

      // Snap to 15 degree increments if shift is held
      if (e.shiftKey) {
        newRotation = Math.round(newRotation / 15) * 15;
      }

      const selectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
      if (selectedShape) {
        onShapeTransform(selectedShapeIds, selectedShape.scale, newRotation);
      }
    }

    // Handle shape resize
    if (activeShapeHandleRef.current && shapeHandleStartRef.current && shapeOriginalParamsRef.current && shapeOriginalPosRef.current && onShapeParamsUpdate && onShapeMove && selectedShapeIds.length > 0) {
      const selectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
      if (selectedShape) {
        const handle = activeShapeHandleRef.current;
        const deltaX = worldPos.x - shapeHandleStartRef.current.x;
        const deltaY = worldPos.y - shapeHandleStartRef.current.y;
        const origParams = shapeOriginalParamsRef.current;
        const origPos = shapeOriginalPosRef.current;

        // Calculate new dimensions based on handle position and shape type
        let newParams: ShapeParams | null = null;
        let newX = origPos.x;
        let newY = origPos.y;

        if (selectedShape.shapeType === 'rectangle') {
          const rectParams = origParams.params as import('../../types/shape').RectangleParams;
          let newWidth = rectParams.width;
          let newHeight = rectParams.height;

          // Corner handles
          if (handle === 'tl') {
            newWidth = rectParams.width - deltaX;
            newHeight = rectParams.height - deltaY;
            newX = origPos.x + deltaX / 2;
            newY = origPos.y + deltaY / 2;
          } else if (handle === 'tr') {
            newWidth = rectParams.width + deltaX;
            newHeight = rectParams.height - deltaY;
            newX = origPos.x + deltaX / 2;
            newY = origPos.y + deltaY / 2;
          } else if (handle === 'br') {
            newWidth = rectParams.width + deltaX;
            newHeight = rectParams.height + deltaY;
            newX = origPos.x + deltaX / 2;
            newY = origPos.y + deltaY / 2;
          } else if (handle === 'bl') {
            newWidth = rectParams.width - deltaX;
            newHeight = rectParams.height + deltaY;
            newX = origPos.x + deltaX / 2;
            newY = origPos.y + deltaY / 2;
          }
          // Edge handles
          else if (handle === 't') {
            newHeight = rectParams.height - deltaY;
            newY = origPos.y + deltaY / 2;
          } else if (handle === 'b') {
            newHeight = rectParams.height + deltaY;
            newY = origPos.y + deltaY / 2;
          } else if (handle === 'l') {
            newWidth = rectParams.width - deltaX;
            newX = origPos.x + deltaX / 2;
          } else if (handle === 'r') {
            newWidth = rectParams.width + deltaX;
            newX = origPos.x + deltaX / 2;
          }

          // Constrain proportions if shift is held
          if (e.shiftKey && (handle === 'tl' || handle === 'tr' || handle === 'br' || handle === 'bl')) {
            const aspectRatio = rectParams.width / rectParams.height;
            if (Math.abs(newWidth / aspectRatio) > Math.abs(newHeight)) {
              newHeight = newWidth / aspectRatio;
            } else {
              newWidth = newHeight * aspectRatio;
            }
          }

          // Ensure minimum size
          newWidth = Math.max(10, Math.abs(newWidth));
          newHeight = Math.max(10, Math.abs(newHeight));

          newParams = {
            type: 'rectangle',
            params: { ...rectParams, width: newWidth, height: newHeight }
          };
        } else if (selectedShape.shapeType === 'ellipse') {
          const ellipseParams = origParams.params as import('../../types/shape').EllipseParams;
          let newRadiusX = ellipseParams.radiusX;
          let newRadiusY = ellipseParams.radiusY;

          if (handle === 'tl' || handle === 'tr' || handle === 'br' || handle === 'bl') {
            const signX = (handle === 'tr' || handle === 'br') ? 1 : -1;
            const signY = (handle === 'br' || handle === 'bl') ? 1 : -1;
            newRadiusX = ellipseParams.radiusX + signX * deltaX / 2;
            newRadiusY = ellipseParams.radiusY + signY * deltaY / 2;
          } else if (handle === 'l' || handle === 'r') {
            const sign = handle === 'r' ? 1 : -1;
            newRadiusX = ellipseParams.radiusX + sign * deltaX / 2;
          } else if (handle === 't' || handle === 'b') {
            const sign = handle === 'b' ? 1 : -1;
            newRadiusY = ellipseParams.radiusY + sign * deltaY / 2;
          }

          // Constrain proportions if shift is held
          if (e.shiftKey && (handle === 'tl' || handle === 'tr' || handle === 'br' || handle === 'bl')) {
            const maxRadius = Math.max(Math.abs(newRadiusX), Math.abs(newRadiusY));
            newRadiusX = maxRadius;
            newRadiusY = maxRadius;
          }

          newRadiusX = Math.max(5, Math.abs(newRadiusX));
          newRadiusY = Math.max(5, Math.abs(newRadiusY));

          newParams = {
            type: 'ellipse',
            params: { radiusX: newRadiusX, radiusY: newRadiusY }
          };
        } else if (selectedShape.shapeType === 'polygon') {
          const polyParams = origParams.params as import('../../types/shape').PolygonParams;
          const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          const sign = (deltaX + deltaY) > 0 ? 1 : -1;
          const newRadius = Math.max(10, polyParams.radius + sign * dist / 2);

          newParams = {
            type: 'polygon',
            params: { ...polyParams, radius: newRadius }
          };
        } else if (selectedShape.shapeType === 'star') {
          const starParams = origParams.params as import('../../types/shape').StarParams;
          const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          const sign = (deltaX + deltaY) > 0 ? 1 : -1;
          const newOuterRadius = Math.max(10, starParams.outerRadius + sign * dist / 2);

          newParams = {
            type: 'star',
            params: { ...starParams, outerRadius: newOuterRadius }
          };
        } else if (selectedShape.shapeType === 'diamond') {
          const diamondParams = origParams.params as import('../../types/shape').DiamondParams;
          let newWidth = diamondParams.width;
          let newHeight = diamondParams.height;

          if (handle === 'tl' || handle === 'tr' || handle === 'br' || handle === 'bl') {
            const signX = (handle === 'tr' || handle === 'br') ? 1 : -1;
            const signY = (handle === 'br' || handle === 'bl') ? 1 : -1;
            newWidth = diamondParams.width + signX * deltaX;
            newHeight = diamondParams.height + signY * deltaY;
          } else if (handle === 'l' || handle === 'r') {
            const sign = handle === 'r' ? 1 : -1;
            newWidth = diamondParams.width + sign * deltaX;
          } else if (handle === 't' || handle === 'b') {
            const sign = handle === 'b' ? 1 : -1;
            newHeight = diamondParams.height + sign * deltaY;
          }

          newWidth = Math.max(10, Math.abs(newWidth));
          newHeight = Math.max(10, Math.abs(newHeight));

          newParams = {
            type: 'diamond',
            params: { width: newWidth, height: newHeight }
          };
        }

        if (newParams) {
          onShapeParamsUpdate(selectedShape.id, newParams);
          // Update position for shapes that need it (rectangles move their center when resizing from edges)
          if (newX !== origPos.x || newY !== origPos.y) {
            const positions = new Map<string, { x: number; y: number }>();
            positions.set(selectedShape.id, { x: newX, y: newY });
            onShapeMove(positions);
          }
        }
      }
    }

    // Handle corner radius dragging
    if (isDraggingCornerRadiusRef.current && cornerRadiusStartPosRef.current && onShapeParamsUpdate && selectedShapeIds.length > 0) {
      const selectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
      if (selectedShape && selectedShape.shapeType === 'rectangle') {
        const deltaX = worldPos.x - cornerRadiusStartPosRef.current.x;
        // Increase radius as mouse moves inward (negative delta)
        const newRadius = Math.max(0, cornerRadiusStartValueRef.current - deltaX);
        const rectParams = selectedShape.shapeParams as { type: 'rectangle'; params: import('../../types/shape').RectangleParams };
        const updatedParams: ShapeParams = {
          type: 'rectangle',
          params: {
            ...rectParams.params,
            cornerRadius: {
              ...rectParams.params.cornerRadius,
              topLeft: newRadius,
              topRight: rectParams.params.cornerRadius.uniform ? newRadius : rectParams.params.cornerRadius.topRight,
              bottomRight: rectParams.params.cornerRadius.uniform ? newRadius : rectParams.params.cornerRadius.bottomRight,
              bottomLeft: rectParams.params.cornerRadius.uniform ? newRadius : rectParams.params.cornerRadius.bottomLeft
            }
          }
        };
        onShapeParamsUpdate(selectedShape.id, updatedParams);
      }
    }

    // Handle star inner radius dragging
    if (isDraggingStarInnerRef.current && starInnerStartPosRef.current && onShapeParamsUpdate && selectedShapeIds.length > 0) {
      const selectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
      if (selectedShape && selectedShape.shapeType === 'star') {
        const starParams = selectedShape.shapeParams as { type: 'star'; params: import('../../types/shape').StarParams };
        const center = { x: selectedShape.x, y: selectedShape.y };
        const startDist = Math.sqrt(Math.pow(starInnerStartPosRef.current.x - center.x, 2) + Math.pow(starInnerStartPosRef.current.y - center.y, 2));
        const currentDist = Math.sqrt(Math.pow(worldPos.x - center.x, 2) + Math.pow(worldPos.y - center.y, 2));
        const ratio = startDist > 0 ? currentDist / starParams.params.outerRadius : 0.5;
        const newRatio = Math.max(0.1, Math.min(0.9, ratio));
        const updatedParams: ShapeParams = {
          type: 'star',
          params: {
            ...starParams.params,
            innerRatio: newRatio
          }
        };
        onShapeParamsUpdate(selectedShape.id, updatedParams);
      }
    }

    // View mode hover detection: update hovered layer for opacity feedback
    if (activeTool === 'pan' && onLayerElementClick) {
      const newHoveredLayerId = findViewModeHoveredLayerId(worldPos.x, worldPos.y);
      if (newHoveredLayerId !== hoveredViewLayerIdRef.current) {
        hoveredViewLayerIdRef.current = newHoveredLayerId;
      }
    } else if (hoveredViewLayerIdRef.current !== null) {
      hoveredViewLayerIdRef.current = null;
    }

    setLastMousePos(currentPos);
  }, [isPanning, lastMousePos, viewport.zoom, isDrawingActive, lastDrawPos, brushSettings, activeTool, activeMeasureTool, screenToWorld, drawStrokesAlongLine, onPropMove, onPropTransform, selectedPropIds, worldToScreen, props, propImages, scale, currentPathPoints, onCurrentPathUpdate, selectedPenPathIds, onPenPathMove, penPaths, onPenPathPointsUpdate, shapes, selectedShapeIds, onShapeMove, onShapeTransform, onShapeParamsUpdate, onLayerElementClick, findViewModeHoveredLayerId]);

  /**
   * Handles mouse up to stop panning or drawing
   */
  const handleMouseUp = useCallback((e: MouseEvent<HTMLCanvasElement>): void => {
    // Stop ruler dragging
    if (activeMeasureTool === 'ruler') {
      isRulerActiveRef.current = false;
    }

    // Handle pending prop click - if we still have a pending click, it means mouse didn't move much
    // This is a simple click, so select the prop
    if (pendingPropClickRef.current) {
      const clickedProp = pendingPropClickRef.current.prop;
      onPropSelect?.([clickedProp.id]);
      pendingPropClickRef.current = null;
      return;
    }

    // Handle selection box completion
    if (isSelectionBoxActiveRef.current && selectionBoxStartRef.current && selectionBoxEndRef.current) {
      const start = selectionBoxStartRef.current;
      const end = selectionBoxEndRef.current;

      // Check if it's a small click (no real drag) - threshold of 5 pixels
      const dragDistance = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
      const minSelectionBoxSize = 5 / viewport.zoom; // 5 screen pixels in world units

      if (dragDistance < minSelectionBoxSize) {
        // This was a click, not a drag - place a prop or deselect
        if (activeTool === 'prop' && propSettings?.selectedProp && onPropPlace && selectedPropIds.length === 0) {
          // Only place prop if zoom level allows it (prop must be visible enough)
          if (canPlacePropAtZoom()) {
            onPropPlace(start.x, start.y);
          }
        } else if (selectedPropIds.length > 0) {
          // Deselect all props
          onPropSelect?.([]);
        }
      } else {
        // This was a real selection box drag - find props in the box
        const propsInBox = findPropsInRect(start.x, start.y, end.x, end.y);
        if (propsInBox.length > 0) {
          // If Shift is held, add to existing selection
          if (e.shiftKey) {
            const newSelection = [...selectedPropIds];
            for (const prop of propsInBox) {
              if (!newSelection.includes(prop.id)) {
                newSelection.push(prop.id);
              }
            }
            onPropSelect?.(newSelection);
          } else {
            // Replace selection
            onPropSelect?.(propsInBox.map(p => p.id));
          }
        } else if (!e.shiftKey) {
          // No props selected and not holding shift - deselect
          onPropSelect?.([]);
        }
      }

      // Clear selection box
      isSelectionBoxActiveRef.current = false;
      selectionBoxStartRef.current = null;
      selectionBoxEndRef.current = null;
    }

    // Stop prop dragging
    if (isDraggingPropRef.current) {
      isDraggingPropRef.current = false;
      dragStartPosRef.current = null;
      multiDragStartRef.current.clear();
    }

    // Stop text dragging
    if (isDraggingTextRef.current) {
      isDraggingTextRef.current = false;
      textDragStartPosRef.current = null;
      textDragOriginalPositionsRef.current.clear();
    }

    // Stop text handle manipulation
    if (activeTextHandleRef.current) {
      activeTextHandleRef.current = null;
      textHandleStartPosRef.current = null;
      textHandleStartSizeRef.current = null;
      textHandleStartCenterRef.current = null;
    }

    // Stop handle manipulation
    if (activeHandleRef.current) {
      activeHandleRef.current = null;
      handleStartPosRef.current = null;
    }

    // Stop multi-transform
    if (isMultiTransformActiveRef.current) {
      isMultiTransformActiveRef.current = false;
      multiTransformStartRef.current = null;
    }

    // Stop pen path handle dragging
    if (isDraggingPenHandleRef.current) {
      isDraggingPenHandleRef.current = false;
      penDragStartPosRef.current = null;
      tempHandleRef.current = null;
    }

    // Stop pen path dragging
    if (isDraggingPenPathRef.current) {
      onPenPathDragEnd?.(selectedPenPathIds);
      isDraggingPenPathRef.current = false;
      penDragStartPosRef.current = null;
      penPathDragStartRef.current.clear();
    }

    // Stop anchor point or handle dragging
    if (editingAnchorRef.current || editingHandleRef.current) {
      editingAnchorRef.current = null;
      editingHandleRef.current = null;
      originalPointRef.current = null;
      penDragStartPosRef.current = null;
    }

    // Stop multi-anchor dragging
    if (isDraggingMultiAnchorsRef.current) {
      isDraggingMultiAnchorsRef.current = false;
      multiAnchorOriginalPositionsRef.current.clear();
      penDragStartPosRef.current = null;
    }

    // Stop inserted point handle creation
    if (isCreatingInsertedPointHandleRef.current) {
      isCreatingInsertedPointHandleRef.current = false;
      insertedPointInfoRef.current = null;
      penDragStartPosRef.current = null;
    }

    // Stop pen path rotation
    if (isRotatingPenPathRef.current) {
      isRotatingPenPathRef.current = false;
      penPathCenterRef.current = null;
      penPathOriginalPointsRef.current.clear();
    }

    // Handle shape creation on mouse up
    if (shapeDrawStartRef.current && shapeDrawCurrentRef.current && activeTool === 'shape' && shapeSettings && onShapePlace) {
      const start = shapeDrawStartRef.current;
      const current = shapeDrawCurrentRef.current;

      // Calculate dimensions
      let width = Math.abs(current.x - start.x);
      let height = Math.abs(current.y - start.y);

      // Minimum size threshold
      if (width > 5 && height > 5) {
        // Constrain proportions if Shift is held
        if (isShiftHeldRef.current) {
          const size = Math.max(width, height);
          width = height = size;
        }

        // Calculate center position
        let centerX: number, centerY: number;
        if (isAltHeldRef.current) {
          // Alt: draw from center
          centerX = start.x;
          centerY = start.y;
        } else {
          // Normal: draw from corner
          centerX = Math.min(start.x, current.x) + width / 2;
          centerY = Math.min(start.y, current.y) + height / 2;
        }

        // Create shape params based on type
        const shapeParams = createDefaultShapeParams(shapeSettings.selectedShapeType, width, height, shapeSettings);

        onShapePlace(shapeSettings.selectedShapeType, shapeParams, centerX, centerY);
      }

      shapeDrawStartRef.current = null;
      shapeDrawCurrentRef.current = null;
      onDrawingShapeChange?.(false);
    }

    // Stop shape dragging
    if (isDraggingShapeRef.current) {
      isDraggingShapeRef.current = false;
      shapeDragStartRef.current = null;
      shapeDragOriginalPosRef.current.clear();
    }

    // Stop shape rotation
    if (isRotatingShapeRef.current) {
      isRotatingShapeRef.current = false;
      shapeCenterRef.current = null;
    }

    // Stop shape handle manipulation
    if (activeShapeHandleRef.current) {
      activeShapeHandleRef.current = null;
      shapeHandleStartRef.current = null;
      shapeOriginalParamsRef.current = null;
      shapeOriginalPosRef.current = null;
    }

    // Stop corner radius dragging
    if (isDraggingCornerRadiusRef.current) {
      isDraggingCornerRadiusRef.current = false;
      cornerRadiusStartPosRef.current = null;
    }

    // Stop star inner radius dragging
    if (isDraggingStarInnerRef.current) {
      isDraggingStarInnerRef.current = false;
      starInnerStartPosRef.current = null;
    }

    // Stop continuous rotation interval
    if (continuousRotationIntervalRef.current) {
      clearInterval(continuousRotationIntervalRef.current);
      continuousRotationIntervalRef.current = null;
    }

    // View mode click detection: when using pan tool and clicked (not dragged), check for element
    // Uses the same precise hit detection as the hover (findViewModeHoveredLayerId)
    // If an element with description is found, trigger the callback
    if (activeTool === 'pan' && !didPanRef.current && onLayerElementClick && mouseDownPosRef.current) {
      const clickWorldPos = mouseDownPosRef.current;

      // Use the hover detection function to find the layerId
      const clickedLayerId = findViewModeHoveredLayerId(clickWorldPos.x, clickWorldPos.y);
      if (clickedLayerId) {
        // We found a layer with description, now we need to find the specific element
        // that was clicked to pass its type and id to the callback
        // For simplicity, we pass 'prop' as type and empty id since the layer is what matters
        onLayerElementClick(clickedLayerId, 'prop', '');
      }
    }

    mouseDownPosRef.current = null;
    setIsPanning(false);
    setIsDrawingActive(false);
    setLastDrawPos(null);
  }, [activeMeasureTool, viewport.zoom, activeTool, propSettings, onPropPlace, onPropSelect, selectedPropIds, findPropsInRect, canPlacePropAtZoom, shapeSettings, onShapePlace, onDrawingShapeChange, onLayerElementClick, findViewModeHoveredLayerId]);

  /**
   * Handles mouse leave
   */
  const handleMouseLeave = useCallback((): void => {
    // Clear view mode hover state
    hoveredViewLayerIdRef.current = null;

    // Stop selection box
    if (isSelectionBoxActiveRef.current) {
      isSelectionBoxActiveRef.current = false;
      selectionBoxStartRef.current = null;
      selectionBoxEndRef.current = null;
    }

    // Stop prop dragging
    if (isDraggingPropRef.current) {
      isDraggingPropRef.current = false;
      dragStartPosRef.current = null;
      multiDragStartRef.current.clear();
    }

    // Stop handle manipulation
    if (activeHandleRef.current) {
      activeHandleRef.current = null;
      handleStartPosRef.current = null;
    }

    // Stop multi-transform
    if (isMultiTransformActiveRef.current) {
      isMultiTransformActiveRef.current = false;
      multiTransformStartRef.current = null;
    }

    // Cancel pending prop click
    pendingPropClickRef.current = null;

    // Stop shape operations
    if (shapeDrawStartRef.current) {
      shapeDrawStartRef.current = null;
      shapeDrawCurrentRef.current = null;
    }
    if (isDraggingShapeRef.current) {
      isDraggingShapeRef.current = false;
      shapeDragStartRef.current = null;
      shapeDragOriginalPosRef.current.clear();
    }
    if (isRotatingShapeRef.current) {
      isRotatingShapeRef.current = false;
      shapeCenterRef.current = null;
    }
    if (activeShapeHandleRef.current) {
      activeShapeHandleRef.current = null;
      shapeHandleStartRef.current = null;
      shapeOriginalParamsRef.current = null;
      shapeOriginalPosRef.current = null;
    }
    if (isDraggingCornerRadiusRef.current) {
      isDraggingCornerRadiusRef.current = false;
      cornerRadiusStartPosRef.current = null;
    }
    if (isDraggingStarInnerRef.current) {
      isDraggingStarInnerRef.current = false;
      starInnerStartPosRef.current = null;
    }

    // Stop continuous rotation interval
    if (continuousRotationIntervalRef.current) {
      clearInterval(continuousRotationIntervalRef.current);
      continuousRotationIntervalRef.current = null;
    }

    mouseDownPosRef.current = null;
    setIsPanning(false);
    setIsDrawingActive(false);
    setLastDrawPos(null);
    setLastMousePos(null);
    setMouseWorldPos(null);
  }, []);

  /**
   * Handles right click to clear/undo measurements
   */
  const handleContextMenu = useCallback((e: MouseEvent<HTMLCanvasElement>): void => {
    e.preventDefault();

    if (activeMeasureTool === 'ruler') {
      rulerStartRef.current = null;
      rulerEndRef.current = null;
      isRulerActiveRef.current = false;
    } else if (activeMeasureTool === 'tape') {
      // Remove last point or clear all
      if (tapePointsRef.current.length > 0) {
        tapePointsRef.current = tapePointsRef.current.slice(0, -1);
        if (tapePointsRef.current.length === 0) {
          tapeTempPointRef.current = null;
        }
      }
    }
  }, [activeMeasureTool]);

  /**
   * Handle Escape key to clear measurements or cancel pen path drawing
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (activeMeasureTool) {
          rulerStartRef.current = null;
          rulerEndRef.current = null;
          isRulerActiveRef.current = false;
          tapePointsRef.current = [];
          tapeTempPointRef.current = null;
        }

        // Cancel pen path drawing
        if (activeTool === 'pen' && isDrawingPath) {
          onDrawingPathChange?.(false);
          onCurrentPathUpdate?.([]);
          tempHandleRef.current = null;
          isDraggingPenHandleRef.current = false;
        }
      }

      // Enter key to finish open path (like double-click)
      if (e.key === 'Enter' && activeTool === 'pen' && isDrawingPath && currentPathPoints.length >= 2) {
        onPenPathPlace?.(currentPathPoints, false); // Open path
        onDrawingPathChange?.(false);
        onCurrentPathUpdate?.([]);
        tempHandleRef.current = null;
        isDraggingPenHandleRef.current = false;
      }

      // Track Alt key for pen tool (corner mode)
      if (e.key === 'Alt') {
        isAltHeldRef.current = true;
      }

      // Delete/Backspace to delete selected anchor points
      if ((e.key === 'Delete' || e.key === 'Backspace') && activeTool === 'pen' && !isDrawingPath) {
        // Check if we have selected anchors
        let hasSelectedAnchors = false;
        for (const indices of selectedAnchorIndices.values()) {
          if (indices.size > 0) {
            hasSelectedAnchors = true;
            break;
          }
        }
        if (hasSelectedAnchors && onDeleteSelectedAnchors) {
          e.preventDefault();
          onDeleteSelectedAnchors();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent): void => {
      if (e.key === 'Alt') {
        isAltHeldRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [activeMeasureTool, activeTool, isDrawingPath, onDrawingPathChange, onCurrentPathUpdate, currentPathPoints, onPenPathPlace, selectedAnchorIndices, onDeleteSelectedAnchors]);

  // Determine cursor style based on tool and state
  /**
   * Handles double-click for inline text editing or finishing pen path
   */
  const handleDoubleClick = useCallback((e: MouseEvent<HTMLCanvasElement>): void => {
    // Pen tool: double-click to finish open path
    if (activeTool === 'pen' && isDrawingPath && currentPathPoints.length >= 2) {
      onPenPathPlace?.(currentPathPoints, false); // Open path
      onDrawingPathChange?.(false);
      tempHandleRef.current = null;
      return;
    }

    if (activeTool !== 'text') return;

    const worldPos = screenToWorld(e.clientX, e.clientY);
    const clickedText = findTextAtPosition(worldPos.x, worldPos.y);

    if (clickedText && onTextStartEdit) {
      onTextStartEdit(clickedText.id);
      setInlineEditValue(clickedText.content);
    }
  }, [activeTool, screenToWorld, findTextAtPosition, onTextStartEdit, isDrawingPath, currentPathPoints, onPenPathPlace, onDrawingPathChange]);

  /**
   * Handles inline text edit submission
   */
  const handleInlineEditSubmit = useCallback(() => {
    if (editingTextId && onTextFinishEdit) {
      onTextFinishEdit(editingTextId, inlineEditValue);
    }
  }, [editingTextId, onTextFinishEdit, inlineEditValue]);

  const getCursorStyle = (): string => {
    if (isPanning) return 'grabbing';
    if (activeTool === 'pan' && !activeMeasureTool) {
      // Show pointer cursor when hovering a clickable element in view mode
      if (hoveredViewLayerIdRef.current) return 'pointer';
      return 'grab';
    }
    if (activeMeasureTool) return 'crosshair';
    return 'crosshair';
  };

  // Get position and style for inline text editor
  const getInlineEditorStyle = (): React.CSSProperties | null => {
    if (!editingTextId) return null;

    const text = texts.find(t => t.id === editingTextId);
    if (!text) return null;

    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const screenX = (text.x - viewport.x) * viewport.zoom + centerX;
    const screenY = (text.y - viewport.y) * viewport.zoom + centerY;
    // For fixed size mode, dimensions stay constant regardless of zoom
    const scaledWidth = text.fixedSize ? text.width : text.width * viewport.zoom;
    const scaledHeight = text.fixedSize ? text.height : text.height * viewport.zoom;
    // Auto-calculate font size based on textbox height (70% of height), matching render logic
    const autoFontSize = text.height * 0.7;
    const scaledFontSize = text.fixedSize ? autoFontSize : autoFontSize * viewport.zoom;
    const padding = text.fixedSize ? 4 : 4 * viewport.zoom;

    // Build text-shadow for stroke effect
    let textShadow = 'none';
    if (text.strokeColor && text.strokeWidth > 0) {
      const strokeW = text.fixedSize ? text.strokeWidth : text.strokeWidth * viewport.zoom;
      // Create multiple shadows to simulate stroke
      const shadows: string[] = [];
      const steps = 8;
      for (let i = 0; i < steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        const x = Math.cos(angle) * strokeW;
        const y = Math.sin(angle) * strokeW;
        shadows.push(`${x}px ${y}px 0 ${text.strokeColor}`);
      }
      textShadow = shadows.join(', ');
    }

    return {
      position: 'absolute',
      left: screenX - scaledWidth / 2,
      top: screenY - scaledHeight / 2,
      width: scaledWidth,
      height: scaledHeight,
      fontSize: scaledFontSize,
      lineHeight: 1.2,
      fontFamily: text.fontFamily,
      fontWeight: text.fontWeight as number,
      color: text.color,
      textAlign: text.alignment,
      background: 'transparent',
      border: '2px dashed rgba(0, 170, 255, 0.5)',
      outline: 'none',
      resize: 'none',
      padding: padding,
      boxSizing: 'border-box' as const,
      transform: `rotate(${text.rotation}deg)`,
      transformOrigin: 'center center',
      zIndex: 1000,
      textShadow,
      caretColor: text.color,
      overflow: 'hidden'
    };
  };

  const inlineEditorStyle = getInlineEditorStyle();

  return (
    <div ref={containerRef} className="map-canvas-container">
      <canvas
        ref={canvasRef}
        className="map-canvas"
        style={{ cursor: getCursorStyle() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      />
      {editingTextId && inlineEditorStyle && (
        <textarea
          ref={textInputRef}
          className="map-canvas-inline-editor"
          style={inlineEditorStyle}
          value={inlineEditValue}
          onChange={(e) => setInlineEditValue(e.target.value)}
          onBlur={handleInlineEditSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onTextFinishEdit?.(editingTextId, texts.find(t => t.id === editingTextId)?.content || '');
            } else if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleInlineEditSubmit();
            }
          }}
          autoFocus
        />
      )}
    </div>
  );
}

export default MapCanvas;
